import { Fragment, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useListQuery, usePagedKey, queryData, isFetching } from "./lib/query";
import { supabase } from "./lib/supabase";
import { Vault, VAULT_SUBS } from "./Vault";
import { resolveTab, resolveVaultSub } from "./lib/tabs";
import { ImportVault } from "./Import";
import { Voted } from "./Voted";
import { Elections } from "./Elections";
import { Home } from "./Home";
import { ConfirmHost, confirmDialog, notify } from "./Confirm";
import { api, ApiError } from "./lib/api";
import { Logo } from "./components/Logo";
import { Versions } from "./Versions";
import { Devices } from "./Devices";
import { MoneyTab } from "./Money";
import { TextingReviews } from "./TextingReviews";
import { CreditCell, CreditTab, money } from "./ClientCredit";
import { Select } from "./components/ui";
import { useFirstLoad } from "./lib/quiet";

const SCOPE_TYPES = ["state", "county", "precinct", "congressional", "state_house", "state_senate", "commissioner", "jp", "city", "school"];
const SCOPE_LABEL: Record<string, string> = {
  state: "Whole state", county: "County", precinct: "Precinct", congressional: "Congress",
  state_house: "State House", state_senate: "State Senate", commissioner: "Commissioner",
  jp: "Justice of Peace", city: "City", school: "School",
  // 0378: one of a city's council districts. Picked under "City", never on its own.
  council: "City council",
  // Present in DISTRICT_TYPES; without an entry here the dropdown literally read "state_board_ed".
  state_board_ed: "State Board of Education",
};

// #204 — district scopes whose value is a real, pickable district. "state" = whole state
// (no value). The rest cascade: pick a TYPE, then pick the actual district from a dropdown
// populated by GET /districts (no free-typing). Counties/precincts come straight from the
// loaded voter data; the dropdown is whatever scope_validate would accept.
// PRECINCT BELONGS HERE AND WAS MISSING. Everything else for it already existed: the database
// accepts scope_type 'precinct' (0194, with a county fips required because precinct 3206 exists in
// several counties), /portal/precincts loads them, SCOPE_LABEL names it, COUNTY_LOCAL knows it
// needs a county first, and the cascade below has a "— pick a precinct —" branch written and
// working. The only thing missing was the type's name in this array, so the dropdown never offered
// it and the whole path was unreachable.
//
// It is the grant that matters most for the races this was built for: a small county race where we
// cannot geocode a district boundary, so the licence is spelled out precinct by precinct.
//
// The other county-local types in SCOPE_TYPES -- commissioner, jp, city, school -- are deliberately
// still absent. They are accepted by the database but nothing loads a list of them, so offering one
// would open a dropdown that is always empty. Add each when its loader exists, not before.
//
// CITY JOINED THE LIST ON 2026-10-25 (0378), once something loaded a list of cities: their limits
// (load_city_limits.py), their council lines (0375) and how each elects its council. It is ONE
// choice for every city race. Caleb: "I'm not thinking about District 4 and then whatever city, I'm
// thinking about the city of San Antonio and then picking the right district." So "City" picks the
// city, says how it elects, and only then -- for a city that has districts -- asks which seat.
// Behind the one choice are two licence types: 'city' (the whole city: mayor, at-large) and
// 'council' (one district). The first dropdown shows "City" for both.
const DISTRICT_TYPES = ["county", "precinct", "state_house", "state_senate", "congressional", "state_board_ed", "city"];
const PLACE_TYPES = new Set(["city", "council"]);
type District = { code: string; name: string | null };

// ── THE CITY LIST (0378) ─────────────────────────────────────────────────────────────────────────
type CityForm = "at_large" | "districts" | "hybrid" | "split";
type City = {
  place: number; name: string; form: CityForm; classified: boolean;
  residency_areas: number | null; confirm: boolean; note: string | null;
  limits: { source: string | null; edited: string | null; loaded: string | null } | null;
  lines: { source: string | null; edited: string | null; loaded: string | null; note: string | null;
           districts: string[] } | null;
};
const FORM_LABEL: Record<CityForm, string> = {
  at_large: "At-large", districts: "Districts", hybrid: "Hybrid", split: "Split",
};
const FORM_SAYS: Record<CityForm, string> = {
  at_large: "Every seat is elected citywide.",
  districts: "Council seats are elected by district.",
  hybrid: "Some seats are elected by district, some citywide.",
  split: "One map, but some seats on it are elected citywide.",
};
const hasSeats = (c: City | undefined) => !!c && c.form !== "at_large";

// Fetched once per state and shared by every picker on the page: ~1,226 rows, and the two pickers
// (open an account, grant a district) ask the same question. A failed fetch is not cached, so the
// next picker asks again instead of inheriting the failure.
const cityCache = new Map<string, Promise<City[]>>();
function loadCities(state: string): Promise<City[]> {
  let p = cityCache.get(state);
  if (!p) {
    p = api<{ cities: City[] }>(`/portal/cities?state=${encodeURIComponent(state)}`).then((r) => r.cities || []);
    p.catch(() => cityCache.delete(state));
    cityCache.set(state, p);
  }
  return p;
}
/** null = still asking, "error" = could not ask (never "no cities"). */
function useCities(state: string, on: boolean): City[] | null | "error" {
  const [cities, setCities] = useState<City[] | null | "error">(null);
  useEffect(() => {
    if (!on) return;
    let dead = false;
    loadCities(state).then((c) => { if (!dead) setCities(c); }).catch(() => { if (!dead) setCities("error"); });
    return () => { dead = true; };
  }, [state, on]);
  return cities;
}

/** What Orator holds about one city, to read back to the client. Only the research's own words and
 *  the sources' own dates -- nothing here is a number this screen made up. Orange is "confirm
 *  this": the research said so, or the lines a district seat needs are not loaded. */
function CityFacts({ state, place, scopeType, district }:
  { state: string; place: number | null; scopeType: string; district: string }) {
  const cities = useCities(state, place != null);
  if (place == null || cities === null) return null;
  if (cities === "error") return <div className="note">Couldn&rsquo;t load what we hold for this city.</div>;
  const c = cities.find((x) => x.place === place);
  if (!c) return null;
  const linesMissing = hasSeats(c) && !c.lines;
  const wantsDistrict = scopeType === "council";
  const box: React.CSSProperties = { border: "1px solid var(--border, #2a3340)", borderRadius: 8,
    padding: "8px 12px", fontSize: 12.5, display: "grid", gap: 3, maxWidth: 780, minWidth: 0,
    overflowWrap: "anywhere" };
  const row = (k: string, v: React.ReactNode) => (
    <div style={{ display: "grid", gridTemplateColumns: "110px minmax(0, 1fr)", gap: 10 }}>
      <span className="muted">{k}</span><span style={{ minWidth: 0 }}>{v}</span></div>);
  // The SOURCE BY ITS SITE'S NAME, linked. Printed whole, an ArcGIS address is one unbroken line
  // wider than the screen, and that alone made the account form scroll sideways (2026-10-25).
  const src = (x: { source: string | null; edited: string | null; loaded: string | null }) => {
    const raw = x.source || "";
    const url = raw.match(/https?:\/\/\S+/)?.[0];
    let host: string | null = null;
    try { host = url ? new URL(url).hostname : null; } catch { host = null; }
    // The loaders write the edit date into the source text too; it is shown once, below.
    const rest = (url ? raw.replace(url, "") : raw).replace(/,?\s*edited \d{4}-\d{2}-\d{2}/, "").trim();
    return <>
      {url ? <a href={url} target="_blank" rel="noreferrer">{host || "source"}</a> : null}
      {rest ? <>{url ? " " : ""}{rest}</> : url ? null : "source not recorded"}
      {x.edited ? ` · edited ${x.edited}` : ""}{x.loaded ? ` · loaded ${x.loaded}` : ""}
    </>;
  };
  return (
    <div style={box}>
      <div style={{ fontWeight: 600 }}>
        {c.name} · {FORM_LABEL[c.form]}
        {c.residency_areas ? ` · ${c.residency_areas} residency areas` : ""}
      </div>
      {row("Council", <>{FORM_SAYS[c.form]}{c.note ? <> {c.note}</> : null}
        {!c.classified && <span className="muted"> (no district seats found by the statewide classification)</span>}</>)}
      {hasSeats(c) && row("District lines", c.lines
        ? <>{c.lines.districts.length} loaded ({c.lines.districts.join(", ")}) · {src(c.lines)}</>
        : <span className="note">Not loaded — the city publishes no usable map, or one we have not traced yet.</span>)}
      {row("City limits", c.limits ? src(c.limits)
        : <span className="note">Not loaded.</span>)}
      {c.residency_areas ? <div className="note">
        A candidate here may say &ldquo;I&rsquo;m running in District 2.&rdquo; Every voter still votes on that seat,
        so the licence is the whole city.</div> : null}
      {wantsDistrict && linesMissing && <div className="note">
        Until the district map is loaded, this account sees the whole city. Ask the client for the map.</div>}
      {c.confirm && <div className="note">The research marks this city&rsquo;s council as one to confirm with the client.</div>}
      <div className="muted">Doors and voters arrive once households are linked to address records (the statewide rebuild after Nov 3).</div>
    </div>
  );
}

// Cascading state -> type -> district. Calls back with (scope_type, scope_value). When the
// chosen type has no loaded districts, shows an inline note instead of an empty silent box.
// 0267 — a licence can now stop at the county line. The county is REQUIRED for the types whose
// code repeats across counties (precinct 3206 exists in several) and OPTIONAL for a district, where
// it narrows the grant to that county's share — the case of a congressional district crossing county
// lines where we hold one of them.
function DistrictPicker({ state, scopeType, scopeValue, scopeFips, scopePlace, onType, onValue, onFips, onPlace,
                         allowState = true }:
  { state: string; scopeType: string; scopeValue: string; scopeFips: number | null; scopePlace: number | null;
    onType: (t: string) => void; onValue: (v: string) => void; onFips: (f: number | null) => void;
    onPlace: (p: number | null) => void; allowState?: boolean }) {
  const [counties, setCounties] = useState<County[] | null>(null);
  const [precincts, setPrecincts] = useState<PrecinctRow[] | null>(null);
  const [list, setList] = useState<District[]>([]);
  // Starts true: this fetch is triggered by picking a scope type, and a false default meant the
  // render immediately after that pick showed "— none loaded —" before setLoading(true) committed.
  const [loading, setLoading] = useState(true);   // loading-ok: initialised true, not false
  const isState = scopeType === "state";
  // Declared up here with isState because the effect below names it in its dependency array, which
  // is evaluated during render -- a const further down would be in the temporal dead zone.
  const isCounty = scopeType === "county";
  const isPlace = PLACE_TYPES.has(scopeType);
  const cities = useCities(state, isPlace);
  useEffect(() => {
    // A county is picked from the county list itself (0308), so this fetch has nothing to add.
    // A city comes from /portal/cities (0378), which also carries its districts.
    if (isState || isCounty || isPlace || !scopeType) { setList([]); setLoading(false); return; }
    let cancel = false; setLoading(true);
    api<{ districts: District[] }>(`/districts?state=${encodeURIComponent(state)}&scope_type=${encodeURIComponent(scopeType)}`)
      .then((r) => { if (!cancel) setList(r.districts || []); })
      .catch(() => { if (!cancel) setList([]); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [state, scopeType, isState, isCounty, isPlace]);
  // EVERY county we hold precinct lines for — all 254 in Texas — not the one whose voters happen
  // to be imported. A licence comes before the data load; offering only imported counties would
  // make it impossible to sell anywhere but Bexar. The list says how much is loaded instead.
  useEffect(() => {
    let cancel = false;
    api<{ counties: County[] }>(`/portal/counties?state=${encodeURIComponent(state)}`)
      .then((r) => { if (!cancel) setCounties(r.counties || []); })
      .catch(() => { if (!cancel) setCounties(null); });   // null = could not ask, not "none"
    return () => { cancel = true; };
  }, [state]);

  const countyLocal = COUNTY_LOCAL.has(scopeType);
  useEffect(() => {
    if (scopeType !== "precinct" || scopeFips == null) { setPrecincts(null); return; }
    let cancel = false;
    api<{ precincts: PrecinctRow[] }>(
      `/portal/precincts?state=${encodeURIComponent(state)}&fips=${scopeFips}`)
      .then((r) => { if (!cancel) setPrecincts(r.precincts || []); })
      .catch(() => { if (!cancel) setPrecincts(null); });
    return () => { cancel = true; };
  }, [state, scopeType, scopeFips]);

  const types = allowState ? ["state", ...DISTRICT_TYPES] : DISTRICT_TYPES;
  return (
    <>
      <select className="ctl" style={{ maxWidth: 170 }} value={isPlace ? "city" : scopeType}
              onChange={(e) => { onType(e.target.value); onValue(""); onPlace(null);
                                 if (!COUNTY_LOCAL.has(e.target.value)) onFips(null); }}>
        {types.map((t) => <option key={t} value={t}>{SCOPE_LABEL[t] || t}</option>)}
      </select>

      {/* ── CITY (0378): the city, then -- only where the city has them -- its seat ──────────── */}
      {isPlace && (() => {
        if (cities === null) return <select className="ctl" disabled><option>Loading…</option></select>;
        if (cities === "error") return <select className="ctl" disabled title="Couldn't load the city list."><option>— couldn&rsquo;t load cities —</option></select>;
        const c = cities.find((x) => x.place === scopePlace);
        const pickCity = (v: string) => {
          const hit = cities.find((x) => String(x.place) === v);
          onPlace(hit ? hit.place : null);
          // A new city starts as the whole city; the seat is asked next, where there is one.
          onType("city"); onValue(hit ? hit.name : "");
        };
        const seatOptions: [string, string][] = c?.lines
          ? [["", "Whole city — mayor or an at-large seat"],
             ...c.lines.districts.map((d) => [d, `District ${d}`] as [string, string])]
          : [];
        return <>
          <Select value={scopePlace == null ? "" : String(scopePlace)} hint="City" onChange={pickCity}
            options={[["", "— pick a city —"] as [string, string],
                      ...cities.map((x) => [String(x.place), `${x.name} · ${FORM_LABEL[x.form]}`] as [string, string])]} />
          {c && !hasSeats(c) && <span className="muted" style={{ fontSize: 12, alignSelf: "center" }}>the whole city</span>}
          {c && hasSeats(c) && c.lines && (
            <Select value={scopeType === "council" ? scopeValue : ""} hint="Seat"
              onChange={(v) => { if (v) { onType("council"); onValue(v); } else { onType("city"); onValue(c.name); } }}
              options={seatOptions} />
          )}
          {/* NO LINES LOADED: there is no list of districts to offer, so the district is typed. The
              licence records the seat; the facts box says the account sees the whole city meanwhile. */}
          {c && hasSeats(c) && !c.lines && (
            <input className="ctl" style={{ width: 150 }} placeholder="District (or blank = whole city)"
              value={scopeType === "council" ? scopeValue : ""}
              onChange={(e) => { const v = e.target.value; if (v.trim()) { onType("council"); onValue(v); } else { onType("city"); onValue(c.name); } }} />
          )}
        </>;
      })()}

      {/* ── A COUNTY LICENCE IS ONE CHOICE (0308) ────────────────────────────────────────────
          Every other scope type asks two questions -- which district, and optionally which
          county's share of it -- so the picker shows two controls. A COUNTY has no share: the
          county IS the licence. This used to render the county dropdown beside a second list
          also headed "county", which made the same answer look like two decisions and left
          scope_value empty unless staff filled both. Here one choice sets the name and the FIPS
          together, which is also the pair the grant is validated on. */}
      {isCounty ? (
        counties === null ? (
          <select className="ctl" disabled title="Couldn't load the county list."><option>— couldn't load counties —</option></select>
        ) : (
          <Select value={scopeFips == null ? "" : String(scopeFips)} hint="County"
                  onChange={(v) => {
                    const hit = (counties || []).find((c) => String(c.fips) === v);
                    onFips(v ? Number(v) : null);
                    // The NAME is what license_scope stores and what the outline is filed under.
                    onValue(hit ? hit.county : "");
                  }}
                  options={[["", "— pick a county —"] as [string, string],
                            ...counties.map((c) => [String(c.fips), `${c.county}${c.households ? "" : " — no voters loaded"}`] as [string, string])]} />
        )
      ) : !isState && !isPlace && (
        /* SEARCHABLE (2026-09-22): 254 Texas counties do not belong in a list you scroll by hand. */
        <span title={countyLocal ? "Required — these codes repeat across counties"
                                 : "Optional — narrows the licence to this county's share of the district"}>
          <Select value={scopeFips == null ? "" : String(scopeFips)} hint="County"
                  onChange={(v) => { onFips(v ? Number(v) : null); onValue(""); }}
                  options={[["", countyLocal ? "— pick a county —" : "All counties"] as [string, string],
                            ...(counties || []).map((c) => [String(c.fips), `${c.county}${c.households ? "" : " — no voters loaded"}`] as [string, string])]} />
        </span>
      )}

      {isPlace ? null : isState ? (
        <input className="ctl" value="(whole state)" disabled />
      ) : isCounty ? (
        <span className="muted" style={{ fontSize: 12, alignSelf: "center" }}>
          the whole county
        </span>
      ) : scopeType === "precinct" ? (
        scopeFips == null
          ? <select className="ctl" disabled><option>— pick a county first —</option></select>
          : precincts === null
            ? <select className="ctl" disabled><option>Loading…</option></select>
            : precincts.length === 0
              ? <select className="ctl" disabled title="No precincts are loaded for that county."><option>— none loaded —</option></select>
              /* The district each precinct sits in is what makes 806 of them pickable, and the
                 household count says how much of it is imported today — a precinct with lines and
                 no voters is a real licence, just not a loaded one. Searchable: 806 rows. */
              : <Select value={scopeValue} onChange={onValue} hint="Precinct"
                  options={[["", "— pick a precinct —"] as [string, string],
                            ...precincts.map((p) => [p.precinct,
                              `${p.precinct}${p.hd ? ` · HD-${p.hd}` : ""}${p.households ? ` · ${p.households} hh` : " · not loaded"}`] as [string, string])]} />
      ) : loading ? (
        <select className="ctl" disabled><option>Loading…</option></select>
      ) : list.length === 0 ? (
        <select className="ctl" disabled title="No districts of this type are loaded for this state."><option>— none loaded —</option></select>
      ) : (
        <Select value={scopeValue} onChange={onValue} hint={SCOPE_LABEL[scopeType] || scopeType}
          options={[["", `— pick ${SCOPE_LABEL[scopeType] || scopeType} —`] as [string, string],
                    ...list.map((d) => [d.code, d.name ? `${d.code} — ${d.name}` : d.code] as [string, string])]} />
      )}
    </>
  );
}

type Account = {
  campaign_id: string; campaign_name: string; org_id: string | null; org_name: string | null;
  state: string; status: string; billing_email: string | null; billing_status: string | null;
  agreement_type: string | null; rate: number | null; rate_period: string | null; holder: string | null;
  scopes: number; members: number; created_at: string | null; holder_user_id: string | null;
  // What the account carries (0279/0307). Optional so an older API build doesn't break the screen;
  // undefined reads as the historical account — voter data, no sign programme.
  voters_enabled?: boolean; places_enabled?: boolean;
  // Prepaid credit for texts and calls (2026-10-26). Optional for an older API build.
  credit_cents?: number;
};
// ONE ROW PER CLIENT, which is what the Client accounts screen has always claimed to be and was
// not. `members` is counted server-side and must not be derived by summing the campaigns': a
// client-level member belongs to every campaign under the client, so adding them up counts one
// person once per campaign.
type Client = { org_id: string; name: string; billing_status: string | null;
                agreement_type: string | null; campaigns: number; active_campaigns: number;
                members: number };
type Scope = { scope_type: string; scope_value: string | null; scope_fips: number | null;
               county: string | null; state: string; granted_at: string | null;
               scope_place?: number | null; place_name?: string | null };
type County = { fips: number; county: string; precincts: number; households: number };
// 0269 — the size of the territory, next to the thing being priced.
type ScopeSize = { scope_type: string; scope_value: string | null; scope_fips: number | null;
                   county: string | null; households: number; voters: number; scope_place?: number | null };
type PrecinctRow = { precinct: string; cd: string | null; sd: string | null; hd: string | null; households: number };
// The types whose code only means something inside a county — precinct 3206 exists in several.
// 'city' left this set in 0378: a city licence names its city (scope_place), not a county.
const COUNTY_LOCAL = new Set(["precinct", "commissioner", "jp", "school"]);
type Finding = { id: string; finding_type: string; precinct: string | null; voter_count: number; detail: any; run_at: string | null };
type ChecklistItem = { item_key: string; label: string; checked: boolean; note: string | null; checked_at: string | null; sort_order: number };

export default function App() {
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [staff, setStaff] = useState<boolean | null>(null);
  const [owner, setOwner] = useState(false);

  const prevUid = useRef<string | null>(null);
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      prevUid.current = data.session?.user?.id ?? null;
      setSession(data.session); setLoading(false);
    });
    // Supabase re-emits SIGNED_IN every time the browser tab regains focus. Only react when
    // the actual signed-in user changes (or signs out) — otherwise resetting `staff` here
    // unmounts the whole Portal and wipes the active tab + any in-flight request on every
    // tab switch. Token refreshes / focus re-emits with the same user are ignored.
    const { data: sub } = supabase.auth.onAuthStateChange((e, s) => {
      const uid = s?.user?.id ?? null;
      if (uid === prevUid.current && e !== "SIGNED_OUT") return;
      prevUid.current = uid;
      setSession(s); setStaff(null);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  // "NOT STAFF" IS A 403, NOT ANY FAILURE.
  //
  // Every error here used to collapse into staff = false, which renders "your account doesn't have
  // platform access" with Sign out as the only button. So a 500, a cold API or a dropped connection
  // locked staff out of the portal during exactly the incident they would be opening it for, told
  // them it was a permissions problem, and offered them the one action that makes it worse.
  const [authErr, setAuthErr] = useState<string | null>(null);
  const [probe, setProbe] = useState(0);
  useEffect(() => {
    if (!session) return;
    setAuthErr(null);
    api<{ staff: boolean; owner?: boolean }>("/portal/whoami")
      .then((r) => { setStaff(!!r.staff); setOwner(!!r.owner); })
      .catch((e: any) => {
        if (e instanceof ApiError && (e.status === 401 || e.status === 403)) { setStaff(false); return; }
        setAuthErr(String(e?.message || e));   // couldn't ask — not the same as being told no
      });
  }, [session, probe]);

  if (loading) return <Center>Loading…</Center>;
  if (!session) return <Login />;
  if (authErr) return (
    <Center>
      <div className="card" style={{ padding: 28, textAlign: "center", maxWidth: 460 }}>
        <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 6 }}>Couldn't check your access</div>
        <p className="muted">This is a connection or server problem, not a permissions one. Your account is fine.</p>
        <p className="muted" style={{ fontSize: 12, wordBreak: "break-word" }}>{authErr}</p>
        <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 10 }}>
          <button className="btn" onClick={() => setProbe((n) => n + 1)}>Try again</button>
          <button className="btn" onClick={() => supabase.auth.signOut()}>Sign out</button>
        </div>
      </div>
    </Center>
  );
  if (staff === null) return <Center>Checking access…</Center>;
  if (!staff) return (
    <Center>
      <div className="card" style={{ padding: 28, textAlign: "center", maxWidth: 420 }}>
        <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 6 }}>Not authorized</div>
        <p className="muted">This portal is for Orator staff only. Your account doesn't have platform access.</p>
        <button className="btn" onClick={() => supabase.auth.signOut()}>Sign out</button>
      </div>
    </Center>
  );
  return <Portal email={session.user?.email} owner={owner} />;
}

function Center({ children }: { children: any }) {
  return <div style={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>{children}</div>;
}

function Login() {
  const [email, setEmail] = useState(""); const [password, setPassword] = useState("");
  const [err, setErr] = useState<string | null>(null); const [busy, setBusy] = useState(false);
  async function submit(e: FormEvent) {
    e.preventDefault(); setErr(null); setBusy(true);
    const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
    if (error) setErr(error.message);
    setBusy(false);
  }
  return (
    <Center>
      <form onSubmit={submit} className="card" style={{ width: 360, padding: 24, display: "flex", flexDirection: "column", gap: 12 }}>
        <div className="row"><span className="brand" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><Logo size={22} />Orator</span><span className="tag">Company Portal</span></div>
        <p className="muted" style={{ margin: 0 }}>Orator staff sign-in.</p>
        <label className="fld">Email<input className="ctl" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} /></label>
        <label className="fld">Password<input className="ctl" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} /></label>
        {err && <div className="err">{err}</div>}
        <button className="btn accent" disabled={busy}>{busy ? "Signing in…" : "Sign in"}</button>
      </form>
    </Center>
  );
}

function Portal({ email, owner }: { email?: string; owner: boolean }) {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [openModal, setOpenModal] = useState(false);
  // The client a new campaign is being opened UNDER, when the flow started from that client's row
  // rather than from "Open account". Null means a fresh start, which is the old behaviour.
  const [addTo, setAddTo] = useState<{ org_id: string; name: string; state?: string } | null>(null);
  const [mergeName, setMergeName] = useState<string | null>(null);
  const [manage, setManage] = useState<Account | null>(null);
  // WHICH THING IS BEING EDITED, not just which row was clicked. This modal has always held the
  // client record, its billing and its contacts; it was only ever opened from a CAMPAIGN's Edit
  // button, and it opened on the client tab — so editing a campaign began on somebody else's
  // fields, and a client could not be opened at all. The scope is what the screen is about.
  const [edit, setEdit] = useState<{ account: Account; scope: "client" | "campaign" } | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [findings, setFindings] = useState<Record<string, number>>({});
  // DID THE REQUEST SUCCEED, separately from what it returned. Home renders "—" rather than a
  // confident 0 when a list is unknown, and now that Portal owns both lists that distinction has
  // to be tracked here or it is lost on the way down: an empty {} and a failed fetch are the same
  // value, and only these flags tell them apart.
  const [accountsOk, setAccountsOk] = useState(false);
  const [findingsOk, setFindingsOk] = useState(false);
  // Clients that exist more than once. A READ, never a repair: merging two orgs hands anybody with
  // org-level membership on one visibility of the other, which is an access change and a person's
  // decision. Shown so the split is visible now rather than discovered the day a manager cannot
  // see a campaign they were promised.
  const [dupClients, setDupClients] = useState<{ name: string; orgs: number; campaigns: number }[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  // WHICH CLIENTS ARE OPEN. Collapsed by default, because the answer to "who are my clients" is the
  // list of names and a screen that opens everything is the campaign list this replaced. A search
  // opens every group it matched, or the hit would be hidden behind a closed row.
  const [openOrgs, setOpenOrgs] = useState<Set<string>>(new Set());
  const [setClientFor, setSetClientFor] = useState<Account | null>(null);
  // THE VAULT'S SUB-TAB LIVES UP HERE NOW, because the rail shows it. It used to be Vault.tsx's
  // own useState; the six vault screens are destinations in the left rail beside the company ones,
  // so the component that draws the rail is the one that has to know which is current. Vault.tsx
  // still owns everything else about them.
  // VALIDATED, NOT JUST DEFAULTED. `|| "overview"` only catches a MISSING value; a retired sub-tab
  // is a present one, and Vault.tsx renders nothing for a name it does not know (0749).
  const [vaultSub, setVaultSub] = useState<string>(
    () => resolveVaultSub(localStorage.getItem("portal.vault")));
  useEffect(() => { localStorage.setItem("portal.vault", vaultSub); }, [vaultSub]);
  const [tab, setTab] = useState<"home" | "accounts" | "team" | "import" | "voted" | "elections" | "audit" | "vault" | "health" | "versions" | "devices" | "money" | "texting">(
    // The persisted-tab cast omitted "addresses", so reloading on that tab silently bounced you to
    // Home. Both tabs are listed now — the cast has to admit every tab the state can hold.
    () => {
      const r = resolveTab(localStorage.getItem("portal.tab"));
      if (r.vaultSub) localStorage.setItem("portal.vault", resolveVaultSub(r.vaultSub));
      return r.tab;
    });
  // Leaving Client accounts leaves the account form too, so coming back shows the list.
  useEffect(() => { if (tab !== "accounts") { setOpenModal(false); setAddTo(null); } }, [tab]);   // eslint-disable-line react-hooks/exhaustive-deps
  // accounts table search / filter / sort
  const [acctQ, setAcctQ] = useState("");
  const [acctStatus, setAcctStatus] = useState("");
  const [sortKey, setSortKey] = useState<"org" | "campaign" | "state" | "scopes" | "members" | "">("");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  useEffect(() => { localStorage.setItem("portal.tab", tab); }, [tab]);

  // A SPINNER ONLY THE FIRST TIME (lib/quiet.ts). Opening, editing, merging or archiving an
  // account re-reads the list; that re-read now swaps the fresh list in without blanking it.
  const firstAccounts = useFirstLoad();
  async function load() {
    if (firstAccounts.isNew("accounts")) setLoading(true);
    setErr(null);
    try {
      setAccounts((await api<{ accounts: Account[] }>("/portal/accounts")).accounts);
      setAccountsOk(true);
      firstAccounts.done("accounts");
      // Non-fatal like the rest: without it the page falls back to listing the campaigns flat,
      // which is worse but is not nothing.
      try { setClients((await api<{ clients: Client[] }>("/portal/clients")).clients || []); }
      catch { setClients([]); }
      try {
        const f = await api<{ findings: { campaign_id: string; open_count: number }[] }>("/portal/findings-summary");
        setFindings(Object.fromEntries(f.findings.map((x) => [x.campaign_id, x.open_count])));
        setFindingsOk(true);
      } catch { setFindingsOk(false); /* non-fatal */ }
      // Non-fatal for the same reason: a notice that cannot load must not take the account list
      // with it. An empty banner is the same screen this page has always shown.
      try {
        setDupClients((await api<{ duplicates: { name: string; orgs: number; campaigns: number }[] }>(
          "/portal/clients/duplicates")).duplicates || []);
      } catch { setDupClients([]); }
    }
    catch (e: any) { setErr(String(e.message || e)); setAccountsOk(false); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function setStatus(a: Account, action: "close" | "reopen") {
    if (action === "close" && !(await confirmDialog(`Close "${a.org_name || a.campaign_name}"? Their logins are disabled and data hidden — reversible.`, { danger: true, confirmLabel: "Close account" }))) return;
    setBusyId(a.campaign_id);
    try { await api(`/portal/accounts/${action}`, { method: "POST", body: JSON.stringify({ campaign_id: a.campaign_id }) }); await load(); }
    catch (e: any) { notify(String(e.message || e)); }
    finally { setBusyId(null); }
  }

  const shown = useMemo(() => {
    let r = accounts;
    if (acctStatus) r = r.filter((a) => a.status === acctStatus);
    const q = acctQ.trim().toLowerCase();
    if (q) r = r.filter((a) => (a.org_name || "").toLowerCase().includes(q)
      || a.campaign_name.toLowerCase().includes(q) || (a.holder || "").toLowerCase().includes(q));
    if (sortKey) {
      const dir = sortDir === "asc" ? 1 : -1;
      const val = (a: Account) => sortKey === "org" ? (a.org_name || a.campaign_name || "").toLowerCase()
        : sortKey === "campaign" ? a.campaign_name.toLowerCase()
        : sortKey === "state" ? a.state
        : sortKey === "scopes" ? a.scopes : a.members;
      r = [...r].sort((a, b) => { const x = val(a), y = val(b); return x < y ? -dir : x > y ? dir : 0; });
    }
    return r;
  }, [accounts, acctQ, acctStatus, sortKey, sortDir]);
  // ── CLIENTS, WITH THEIR CAMPAIGNS UNDERNEATH ──────────────────────────────────────────────────
  //
  // The table under this used to be one row per CAMPAIGN with the client's name repeated in the
  // first column. A client running two campaigns therefore appeared twice, which is visually
  // identical to the duplicate-client problem the banner above warns about — so a correct merge
  // looked like a failed one. Reported 2026-09-18, minutes after a merge that had in fact worked.
  //
  // Campaigns with NO client are their own group rather than being hidden or silently folded into
  // one: "this campaign belongs to nobody" is a real state with a real fix, and it needs somewhere
  // to be seen and acted on.
  const groups = useMemo(() => {
    const byOrg = new Map<string, Account[]>();
    for (const a of shown) {
      const k = a.org_id || "";
      if (!byOrg.has(k)) byOrg.set(k, []);
      byOrg.get(k)!.push(a);
    }
    const meta = new Map(clients.map((c) => [c.org_id, c]));
    const out = [...byOrg.entries()]
      .filter(([k]) => k !== "")
      .map(([org_id, camps]) => ({
        org_id,
        name: meta.get(org_id)?.name || camps[0].org_name || "Client",
        // COUNTED SERVER-SIDE, never summed from the campaigns — see the note on the Client type.
        // Falls back to a dash rather than a wrong number when /portal/clients did not load.
        members: meta.get(org_id)?.members ?? null,
        camps,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
    const orphans = byOrg.get("") || [];
    return { out, orphans };
  }, [shown, clients]);

  // A search must not be answered by a closed row. Typing opens what it found; clearing returns
  // the groups to however the user had them.
  const searching = acctQ.trim().length > 0;
  const isOpen = (id: string) => searching || openOrgs.has(id);
  const toggleOrg = (id: string) => setOpenOrgs((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  const sortBy = (k: typeof sortKey) => { if (sortKey === k) setSortDir((d) => d === "asc" ? "desc" : "asc"); else { setSortKey(k); setSortDir("asc"); } };
  const arrow = (k: typeof sortKey) => sortKey === k ? (sortDir === "asc" ? " ▲" : " ▼") : "";

  return (
    <>
      <ConfirmHost />
      {/* ── A LEFT RAIL, NOT A ROW OF TABS ─────────────────────────────────────────────────────
           Every navigation control used to be a horizontal band across the top, and the vault
           screens added a SECOND band under it. On the address-review screen that is ten stacked
           strips before the satellite view begins, and the map -- the thing the work is actually
           done in -- starts below the fold:

               "it's creating a lot of vertical space that pushes the map and what I'm doing
                lower and I have to scroll"   -- Caleb, 2026-09-09

           Vertical space is the scarce axis on a map screen and horizontal space is not, so the
           navigation moves to the axis that has room. Same shape as the dashboard's AppShell --
           grid, sticky aside, sectioned list -- because the portal already loads that Tailwind
           config and index.css says new components should carry Tailwind classes exactly as the
           dashboard's do.

           THE VAULT'S SIX SCREENS ARE IN THE RAIL AS DESTINATIONS, not behind a "Vault" button
           that then reveals a second row. That removes both bands at once, and it is the honest
           shape: they are six places to go, not a mode with sub-modes. */}
      <div className="grid grid-cols-[216px_1fr] min-h-screen">
        <aside className="bg-surface border-r border-border flex flex-col sticky top-0 h-screen overflow-y-auto">
          <div className="flex items-center gap-2 px-4 pt-4 pb-1 font-extrabold text-lg tracking-tight">
            <Logo size={22} /> Orator
          </div>
          <div className="px-4 pb-3"><span className="tag">Company Portal</span></div>
          <nav className="flex-1">
            <RailSection label="Company" />
            <RailItem on={tab === "home"}     onClick={() => setTab("home")}>Home</RailItem>
            <RailItem on={tab === "accounts"} onClick={() => setTab("accounts")}>Client accounts</RailItem>
            <RailItem on={tab === "money"}    onClick={() => setTab("money")}>Money</RailItem>
            <RailItem on={tab === "texting"}  onClick={() => setTab("texting")}>Texting reviews</RailItem>
            <RailItem on={tab === "team"}     onClick={() => setTab("team")}>Team</RailItem>
            <RailItem on={tab === "audit"}    onClick={() => setTab("audit")}>Activity log</RailItem>
            <RailItem on={tab === "import"}   onClick={() => setTab("import")}>Import</RailItem>
            <RailItem on={tab === "voted"}    onClick={() => setTab("voted")}>Who already voted</RailItem>
            <RailItem on={tab === "elections"} onClick={() => setTab("elections")}>Election calendar</RailItem>
            <RailSection label="Address vault" />
            {VAULT_SUBS.map((v) => (
              <RailItem key={v.v} on={tab === "vault" && vaultSub === v.v}
                        onClick={() => { setTab("vault"); setVaultSub(v.v); }}>{v.label}</RailItem>
            ))}
            <RailSection label="System" />
            <RailItem on={tab === "health"}   onClick={() => setTab("health")}>Health</RailItem>
            <RailItem on={tab === "versions"} onClick={() => setTab("versions")}>Deployed</RailItem>
            <RailItem on={tab === "devices"}  onClick={() => setTab("devices")}>Field devices</RailItem>
          </nav>
          <div className="px-4 py-3 border-t border-border flex items-center gap-2">
            <span className="muted" style={{ fontSize: 12, overflow: "hidden", textOverflow: "ellipsis" }}>{email}</span>
            <button className="btn sm" style={{ marginLeft: "auto" }}
                    onClick={() => supabase.auth.signOut()}>Sign out</button>
          </div>
        </aside>
        <main className="min-w-0">
      {/* Open account / Add campaign is a page in this slot, not a pop-up over it (2026-10-25). */}
      {tab === "accounts" && (openModal || addTo) && (
        <OpenAccountPage into={addTo} onClose={() => { setOpenModal(false); setAddTo(null); }}
                         onDone={() => { setOpenModal(false); setAddTo(null); load(); }} />
      )}
      {tab === "accounts" && !openModal && !addTo && (<div className="wrap">
        {dupClients.length > 0 && (
          <div style={{ border: "1px solid var(--warn,#eab308)", borderRadius: 8, padding: 10, marginBottom: 12 }}>
            <div className="fld" style={{ marginBottom: 4 }}>
              {dupClients.length === 1 ? "One client name is used by two or more separate clients"
                                       : `${dupClients.length} client names are used by two or more separate clients`}
            </div>
            <div className="muted" style={{ fontSize: 13, marginBottom: 8 }}>
              Each is a separate client, so an admin on one does not see the other's campaigns —
              which is the one thing the client grouping exists to do. Combining them changes who
              can see what, so it is never done automatically; the button shows you exactly what
              would change before anything does.
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {dupClients.map((d) => (
                <div key={d.name} className="row" style={{ gap: 10, alignItems: "baseline" }}>
                  <span style={{ fontSize: 13 }}>
                    <strong>{d.name}</strong>
                    <span className="muted"> — {d.orgs} clients, {d.campaigns} campaigns</span>
                  </span>
                  <button className="btn sm" style={{ marginLeft: "auto" }}
                          onClick={() => setMergeName(d.name)}>Review and combine</button>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="row" style={{ marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
          <h2 style={{ margin: 0 }}>Client accounts</h2>
          <span className="muted">
            {groups.out.length} {groups.out.length === 1 ? "client" : "clients"} · {shown.length}
            {shown.length !== accounts.length ? ` of ${accounts.length}` : ""} campaigns
          </span>
          <input className="ctl" style={{ maxWidth: 220 }} placeholder="Search client / campaign / admin" aria-label="Search accounts by client, campaign or admin" name="account-search" value={acctQ} onChange={(e) => setAcctQ(e.target.value)} />
          <select className="ctl" style={{ maxWidth: 150 }} value={acctStatus} onChange={(e) => setAcctStatus(e.target.value)}>
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="closed">Closed</option>
          </select>
          <button className="btn accent" style={{ marginLeft: "auto" }} onClick={() => setOpenModal(true)}>＋ Open account</button>
        </div>
        {err && <div className="err" style={{ marginBottom: 10 }}>{err}</div>}
        <div className="card">
          <table>
            <thead><tr>
              <th style={{ width: 28 }}></th>
              <th style={{ cursor: "pointer" }} onClick={() => sortBy("campaign")}>Campaign{arrow("campaign")}</th>
              <th style={{ cursor: "pointer" }} onClick={() => sortBy("state")}>State{arrow("state")}</th>
              <th>Account holder</th>
              <th style={{ cursor: "pointer" }} onClick={() => sortBy("scopes")}>Districts{arrow("scopes")}</th>
              <th style={{ cursor: "pointer" }} onClick={() => sortBy("members")}>Members{arrow("members")}</th>
              <th>Credit</th>
              <th>Status</th><th></th>
            </tr></thead>
            <tbody>
              {loading && <tr><td colSpan={9} className="muted">Loading…</td></tr>}
              {/* Never invite someone to create an account we merely failed to fetch — that is how
                  you end up with a duplicate client. */}
              {!loading && accounts.length === 0 && (
                <tr><td colSpan={9} className="muted" style={err ? { color: "var(--warn,#eab308)" } : undefined}>
                  {err ? "Couldn't load accounts — this is not an empty list. Retry above." : "No accounts yet. Click “Open account”."}
                </td></tr>
              )}
              {!loading && accounts.length > 0 && shown.length === 0 && <tr><td colSpan={9} className="muted">No accounts match these filters.</td></tr>}
              {/* A CAMPAIGN ROW. Rendered under its client rather than beside a repeated client
                  name — see the note on `groups`. The leading cell is the indent the chevron sits
                  above, so a campaign never reads as a client. */}
              {groups.out.map((g) => (
                <Fragment key={g.org_id}>
                  <tr onClick={() => toggleOrg(g.org_id)} style={{ cursor: "pointer" }}>
                    <td className="muted" style={{ textAlign: "center" }}>{isOpen(g.org_id) ? "▾" : "▸"}</td>
                    <td style={{ fontWeight: 700 }} colSpan={3}>
                      {g.name}
                      <span className="muted" style={{ fontWeight: 400 }}>
                        {" "}— {g.camps.length} {g.camps.length === 1 ? "campaign" : "campaigns"}
                      </span>
                    </td>
                    <td className="muted">{g.camps.reduce((n, c) => n + c.scopes, 0)}</td>
                    {/* A DASH, NOT A SUM. One client-level member belongs to every campaign under
                        the client, so adding the campaign numbers counts them once per campaign —
                        5 people would read as 25 here. The real count comes from /portal/clients;
                        if that did not load, say nothing rather than something wrong. */}
                    <td className="muted">{g.members ?? "—"}</td>
                    <td className="muted" style={{ fontVariantNumeric: "tabular-nums" }}>{money(g.camps.reduce((n, c) => n + (c.credit_cents ?? 0), 0))}</td>
                    <td></td>
                    {/* THE CLIENT'S OWN EDIT. Its details, billing and contacts were only reachable
                        through one of its campaigns, and which campaign you went through made no
                        difference to what you changed — a reliable sign the control was on the
                        wrong object. stopPropagation so the button does not also toggle the row. */}
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      {/* ADD A CAMPAIGN WHERE SOMEBODY IS ALREADY STANDING (2026-09-24). The flow
                          existed and could only be found by typing a name that happened to collide
                          with this client's. Now it starts from the client itself, which is where
                          the thought starts. */}
                      <button className="btn sm" title={`Open another campaign under ${g.name}`}
                        onClick={(e) => { e.stopPropagation();
                          setAddTo({ org_id: g.org_id, name: g.name,
                                     state: g.camps[0]?.state || "TX" }); }}>＋ Add campaign</button>{" "}
                      <button className="btn sm" onClick={(e) => { e.stopPropagation();
                        setEdit({ account: g.camps[0], scope: "client" }); }}>Edit client</button>
                    </td>
                  </tr>
                  {isOpen(g.org_id) && g.camps.map((a) => (
                    <tr key={a.campaign_id}>
                      <td></td>
                      <td style={{ paddingLeft: 18 }}>{a.campaign_name}</td>
                      <td>{a.state}</td>
                      <td className="muted">{a.holder || "—"}</td>
                      <td>{a.scopes}</td>
                      <td>{a.members}</td>
                      <CreditCell cents={a.credit_cents} />
                      <td><span className={"badge " + (a.status === "active" ? "active" : "closed")}>{a.status}</span>
                        {findings[a.campaign_id] > 0 && <span className="chip" title="Precincts with no boundary loaded — open Manage · Setup" style={{ marginLeft: 6, color: "#e08b3f" }}>⚠ {findings[a.campaign_id]}</span>}</td>
                      <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                        <button className="btn sm" onClick={() => setEdit({ account: a, scope: "campaign" })}>Edit</button>{" "}
                        <button className="btn sm" onClick={() => setManage(a)} title="Licensed turf, access and setup for this client">Manage</button>{" "}
                        <button className="btn sm" onClick={() => setSetClientFor(a)}>Move</button>{" "}
                        {a.status === "active"
                          ? <button className="btn sm warn" disabled={busyId === a.campaign_id} onClick={() => setStatus(a, "close")}>Close</button>
                          : <button className="btn sm" disabled={busyId === a.campaign_id} onClick={() => setStatus(a, "reopen")}>Reopen</button>}
                      </td>
                    </tr>
                  ))}
                </Fragment>
              ))}
              {/* CAMPAIGNS THAT BELONG TO NOBODY. Their own group, named as the state it is, with
                  the fix on the row. Hiding them or folding them into a client is how one ends up
                  billed to nobody. */}
              {groups.orphans.length > 0 && (
                <Fragment>
                  <tr>
                    <td className="muted" style={{ textAlign: "center" }}>—</td>
                    <td style={{ fontWeight: 700 }} colSpan={8}>
                      No client
                      <span className="muted" style={{ fontWeight: 400 }}>
                        {" "}— {groups.orphans.length} {groups.orphans.length === 1 ? "campaign is" : "campaigns are"} not
                        under any client, so nobody has client-wide access to {groups.orphans.length === 1 ? "it" : "them"} and
                        {groups.orphans.length === 1 ? " it is" : " they are"} billed to nobody.
                      </span>
                    </td>
                  </tr>
                  {groups.orphans.map((a) => (
                    <tr key={a.campaign_id}>
                      <td></td>
                      <td style={{ paddingLeft: 18 }}>{a.campaign_name}</td>
                      <td>{a.state}</td>
                      <td className="muted">{a.holder || "—"}</td>
                      <td>{a.scopes}</td>
                      <td>{a.members}</td>
                      <CreditCell cents={a.credit_cents} />
                      <td><span className={"badge " + (a.status === "active" ? "active" : "closed")}>{a.status}</span></td>
                      <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                        <button className="btn sm accent" onClick={() => setSetClientFor(a)}>Set client</button>{" "}
                        <button className="btn sm" onClick={() => setEdit({ account: a, scope: "campaign" })}>Edit</button>{" "}
                        <button className="btn sm" onClick={() => setManage(a)} title="Licensed turf, access and setup for this client">Manage</button>
                      </td>
                    </tr>
                  ))}
                </Fragment>
              )}
            </tbody>
          </table>
        </div>
      </div>)}
      {tab === "home" && (
        <Home
          onGo={(t) => setTab(t as any)}
          accounts={accounts}
          accountsOk={accountsOk}
          openFindings={findingsOk ? Object.values(findings).reduce((a, b) => a + b, 0) : null}
        />
      )}
      {tab === "team" && <StaffTab owner={owner} />}
      {tab === "import" && <ImportVault onGo={(t) => setTab(t as any)} />}
      {tab === "audit" && <AuditLog />}
      {tab === "vault" && <Vault sub={vaultSub} onSub={setVaultSub} />}
      {tab === "health" && <Health />}
      {tab === "versions" && <Versions />}
      {tab === "devices" && <Devices />}
      {tab === "money" && <MoneyTab />}
      {tab === "texting" && <TextingReviews />}
      {tab === "voted" && <Voted />}
      {tab === "elections" && <Elections />}
        </main>
      </div>
      {manage && <ManageModal account={manage} onClose={() => { setManage(null); load(); }} />}
      {edit && <EditModal account={edit.account} scope={edit.scope}
                          onClose={() => setEdit(null)} onDone={() => { setEdit(null); load(); }} />}
      {mergeName && <MergeModal name={mergeName} accounts={accounts} onClose={() => setMergeName(null)} onDone={() => { setMergeName(null); load(); }} />}
      {setClientFor && <SetClientModal account={setClientFor} clients={clients}
                                       onClose={() => setSetClientFor(null)}
                                       onDone={() => { setSetClientFor(null); load(); }} />}
    </>
  );
}

type ScopeRow = { scope_type: string; scope_value: string; scope_fips: number | null; scope_place: number | null };

/** ADDING A CAMPAIGN TO A CLIENT YOU ALREADY HAVE (2026-09-24).
 *
 *  Caleb: "there does not appear to be an easy way to add a campaign (or sign campaign) to an
 *  existing client in the company portal."
 *
 *  The PATH existed -- opening an account into an existing client is the same act as adding a
 *  campaign, and has been one flow rather than two buttons since 0263. But the only way to reach
 *  it was to press "Open account", type the client's name closely enough to match, wait for the
 *  debounced lookup, and notice a radio button. A flow you can only find by accidentally
 *  colliding with a name you already own is not a flow anybody finds twice.
 *
 *  `into` seeds it directly from the client's own row, which is where somebody is standing when
 *  they think "this client needs another campaign". The name is then fixed rather than typed --
 *  editing it would mean a different client, which is a different act with its own button. */
function OpenAccountPage({ onClose, onDone, into }: {
  onClose: () => void; onDone: () => void;
  into?: { org_id: string; name: string; state?: string } | null;
}) {
  const [orgName, setOrgName] = useState(into?.name || ""); const [state, setState] = useState(into?.state || "TX");
  const [campaignName, setCampaignName] = useState(""); const [billing, setBilling] = useState("");
  const [adminEmail, setAdminEmail] = useState(""); const [adminName, setAdminName] = useState("");
  // DOES THE CHOSEN CLIENT ALREADY HAVE SOMEBODY? Only meaningful on the add-a-campaign path.
  // true/false is an answer; "error" is NOT an answer and must not be read as either -- a failed
  // check says nothing about the client, so it neither blocks staff nor lets them skip the admin
  // on a client that turns out to be empty. The server decides that case and says why.
  const [seated, setSeated] = useState<boolean | "checking" | "error" | null>(null);
  const [scopes, setScopes] = useState<ScopeRow[]>([{ scope_type: "state_house", scope_value: "", scope_fips: null, scope_place: null }]);
  // VOLUNTEER PREFIXES (0261). The handle namespace is global, permanent and non-recyclable, so
  // Orator allocates it rather than letting the first campaign to type a name take it. Opening the
  // account is when staff are already deciding who this client is; granting none is a normal state
  // and the manager's screen says so instead of offering a box that can only fail.
  // ONE PREFIX IS REQUIRED TO OPEN AN ACCOUNT. Caleb: "I want to make assigning one quick
  // volunteer name a requirement when opening an account, so that every account gets one ...
  // without an acceptable prefix name then you can't open the account." An account opened without
  // one is an account whose manager finds a dead control on their Users screen the first Saturday
  // they need volunteers, and staff are not there to fix it at a table.
  // AN EXISTING CLIENT, CHOSEN DELIBERATELY. 0034 built the org as the client grouping "so the
  // client admin automatically sees every campaign we add for that client, now or later" -- and
  // the later path was never built, so opening a second account made a second CLIENT. Production
  // has "Caleb Cashdollar" twice because of it.
  //
  // Opening an account INTO an existing client is the same act as adding a campaign, so there is
  // one flow rather than two buttons. Never forbidden, only answered: two real clients may share
  // a name, so staff are shown the match and choose.
  type ClientMatch = { org_id: string; name: string; campaigns: number; created_at: string | null };
  const [matches, setMatches] = useState<ClientMatch[]>([]);
  // NO MATCHES AND COULD-NOT-ASK ARE DIFFERENT ANSWERS, and the empty-vs-error guard caught me
  // conflating them. An empty list here reads as "this is a new client", which is a CLAIM -- and
  // if it is wrong, staff open the duplicate this whole check exists to prevent.
  const [matchErr, setMatchErr] = useState<string | null>(null);
  const [intoOrg, setIntoOrg] = useState<string | null>(into?.org_id || null);   // null = a new client
  // Seeded from the client's row, this is settled before the screen opens: the name lookup below
  // must not be able to unset it, and the "a client by this name exists" chooser has nothing left
  // to ask.
  const locked = !!into;
  const [pfxs, setPfxs] = useState<string[]>([""]);
  type PfxCheck = { prefix: string; shape_ok: boolean; available: boolean; holder: string | null; logins?: number };
  // Keyed by the typed string, not by row index, so editing row 1 cannot show row 2's verdict.
  const [pfxChecks, setPfxChecks] = useState<Record<string, PfxCheck | "checking" | "error">>({});
  const [busy, setBusy] = useState(false); const [err, setErr] = useState<string | null>(null);
  // WHAT THIS ACCOUNT BUYS (0307). Caleb, 2026-09-23: "if I give him access to the yard sign tool
  // for Bexar County I don't want him to have the voter data ... it would be an account with just
  // the yard sign tools open for him." Until 0307 a licence WAS the voter grant -- every voter
  // reader reached the vault by joining license_scope -- so there was no way to sell one without
  // the other. "full" is what Orator has always opened and stays the default.
  const [product, setProduct] = useState<"full" | "signs">("full");
  const signsOnly = product === "signs";

  const setScope = (i: number, k: keyof ScopeRow, v: string | number | null) =>
    setScopes((p) => p.map((s, j) => j === i ? { ...s, [k]: v } : s));

  // Debounced for the same reason as the prefix check below. A client name is not unique and is
  // not being validated -- this is a "did you mean the one that already exists" question.
  useEffect(() => {
    if (locked) { setMatches([]); setMatchErr(null); return; }   // the client is already decided
    const nm = orgName.trim();
    if (!nm) { setMatches([]); setMatchErr(null); setIntoOrg(null); return; }
    const t = setTimeout(() => {
      api<{ clients: ClientMatch[] }>(`/portal/clients/named?name=${encodeURIComponent(nm)}`)
        .then((r) => { setMatches(r.clients || []); setMatchErr(null); })
        // Said, not swallowed -- but it does NOT block opening the account. Being unable to check
        // is a reason to warn, not a reason to stop staff working.
        .catch((e) => { setMatches([]); setMatchErr(String(e?.message || e)); });
    }, 350);
    return () => clearTimeout(t);
  }, [orgName, locked]);

  // Choosing a client that is no longer in the list (the name was edited) must not leave a stale
  // org id riding along on the submit.
  useEffect(() => {
    if (locked) return;   // seeded from the client's row, not from the name box
    if (intoOrg && !matches.some((m) => m.org_id === intoOrg)) setIntoOrg(null);
  }, [matches, intoOrg, locked]);

  // ── IS THERE ALREADY SOMEBODY UNDER THIS CLIENT ────────────────────────────────────────────────
  // A campaign added under a client that already has an ACTIVE client-wide manager needs no new
  // admin: org-level membership names no campaign, so that person gets this campaign the moment it
  // exists. A client whose only manager was withdrawn is as empty as one that never had one, which
  // is why this counts is_active rather than rows.
  useEffect(() => {
    if (!intoOrg) { setSeated(null); return; }
    let live = true;
    setSeated("checking");
    api<{ managers: { is_active: boolean; name?: string; display_name?: string; login_id?: string }[] }>(
      `/portal/client/managers?org_id=${encodeURIComponent(intoOrg)}`)
      .then((r) => { if (live) setSeated((r.managers || []).some((m) => m.is_active)); })
      .catch(() => { if (live) setSeated("error"); });
    return () => { live = false; };
  }, [intoOrg]);

  // Debounced so typing a prefix does not fire a request per keystroke. Staff get the holder's
  // name on a taken one -- they can already see every account, and reallocating needs to know.
  useEffect(() => {
    const want = pfxs.map((p) => p.trim().toLowerCase()).filter(Boolean)
                     .filter((p) => pfxChecks[p] === undefined);
    if (!want.length) return;
    const t = setTimeout(() => {
      for (const p of want) {
        setPfxChecks((c) => ({ ...c, [p]: "checking" }));
        api<PfxCheck>(`/portal/prefix/check?prefix=${encodeURIComponent(p)}`)
          .then((r) => setPfxChecks((c) => ({ ...c, [p]: r })))
          // A FAILED CHECK IS NOT AN AVAILABLE PREFIX. Opening the account stays blocked, because
          // allocating a name nobody verified is how two campaigns end up holding one namespace.
          .catch(() => setPfxChecks((c) => ({ ...c, [p]: "error" })));
      }
    }, 350);
    return () => clearTimeout(t);
  }, [pfxs]);   // eslint-disable-line react-hooks/exhaustive-deps

  const pfxList = pfxs.map((p) => p.trim().toLowerCase()).filter(Boolean);
  const pfxOk = pfxList.length > 0
    && pfxList.every((p) => { const c = pfxChecks[p]; return c && c !== "checking" && c !== "error" && c.available; });
  // AN ACCOUNT WITH NOBODY IN IT IS NOT AN ACCOUNT (2026-09-19). The admin email is required, with
  // one exemption: adding a campaign under a client that already has somebody at client level.
  // "checking" is not an answer, so it blocks briefly rather than letting a submit race the check.
  // "error" is not an answer either and must not become one — it does not block, and the server
  // refuses with a clear message if the admin really was needed.
  //
  // NO PASSWORD FIELD SINCE 2026-09-19. This form used to mint the client admin's credential for
  // them, the only place in Orator that did; the admin is now emailed a link and sets their own,
  // like every other named user. Inserting a login directly in SQL remains the backstop for
  // somebody with no working inbox.
  const adminExempt = seated === true || seated === "error";
  const adminOk = !!adminEmail.trim() || adminExempt;
  const valid = orgName.trim() && campaignName.trim() && adminOk && seated !== "checking" && pfxOk;

  async function submit() {
    setBusy(true); setErr(null);
    try {
      const r = await api<{ note?: string; admin_created: boolean }>("/portal/accounts/open", {
        method: "POST", body: JSON.stringify({
          org_name: orgName.trim(), state, campaign_name: campaignName.trim(),
          org_id: intoOrg,
          billing_email: billing.trim() || null, admin_email: adminEmail.trim() || null, admin_name: adminName.trim() || null,
          scopes: scopes.filter((s) => s.scope_type && (s.scope_value.trim() || s.scope_type === "state"))
            // The county and the city travel with the grant. The county was being dropped here, so a
            // precinct picked while opening an account was refused for having no county.
            .map((s) => ({ scope_type: s.scope_type, scope_value: s.scope_value.trim() || null,
                           scope_fips: s.scope_fips, scope_place: s.scope_place })),
          prefixes: pfxList,
          // Every account has the sign programme (0417); a signs-only account has nothing else.
          voters_enabled: !signsOnly, places_enabled: true,
        }),
      });
      // The success path used to be silent: `note` was returned by the API and never populated, so
      // the modal simply closed and staff had no idea whether the admin login was created or reused.
      notify(r.note || (r.admin_created ? "Account opened — admin login created." : "Account opened."));
      onDone();
    } catch (e: any) { setErr(String(e.message || e)); setBusy(false); }
  }

  // ITS OWN PAGE SINCE 2026-10-25. Caleb, trying the City picker in the pop-up: "it's now
  // insufficient for the setup, it probably needs to be its own page ... we have to scroll right to
  // left, that's unacceptable." Same fields in the same order; the page gives the picker and its
  // facts box room, keeps the portal menu, and Back returns to Client accounts with nothing saved.
  return (
    <div className="wrap acct-page">
      <button className="linkback" onClick={onClose}>← Client accounts</button>
      <div className="row" style={{ gap: 10, alignItems: "baseline", flexWrap: "wrap", margin: "6px 0 14px" }}>
        <h2 style={{ margin: 0 }}>{intoOrg ? "Add a campaign" : "Open a client account"}</h2>
        {locked && <span className="muted">under {orgName} · {state}</span>}
      </div>
        <div className="acct-body">
          <div className="grid2">
            <label className="fld">Client{locked ? "" : " name"}
              <input className="ctl" value={orgName} disabled={locked}
                     onChange={(e) => setOrgName(e.target.value)} placeholder="Acme PAC" /></label>
            <label className="fld">State<input className="ctl" value={state} onChange={(e) => setState(e.target.value.toUpperCase())} maxLength={2} /></label>
          </div>
          {/* ── A CLIENT BY THIS NAME ALREADY EXISTS ────────────────────────────────────────
              Not a validation error. Two real clients may share a name, and the existing one is
              usually the right answer -- a client's second campaign belongs under the client, so
              their admin keeps seeing all of it. Choosing one here turns "open an account" into
              "add a campaign", which is the same act. */}
          {!locked && matchErr && (
            <div className="muted" style={{ fontSize: 12, color: "var(--warn,#eab308)" }}>
              Couldn't check whether a client by this name already exists — {matchErr}. You can still
              open the account; just be aware this may duplicate an existing client.
            </div>
          )}
          {!locked && matches.length > 0 && (
            <div style={{ border: "1px solid var(--border,#2a3340)", borderRadius: 8, padding: 10 }}>
              <div className="fld" style={{ marginBottom: 4 }}>
                {matches.length === 1 ? "A client by this name already exists" : `${matches.length} clients already have this name`}
              </div>
              <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
                Put this campaign under the existing client and their admin keeps seeing everything
                in one place. Start a separate client only if this really is a different organisation.
              </div>
              {matches.map((m) => (
                <label key={m.org_id} className="row" style={{ marginBottom: 6, cursor: "pointer", alignItems: "baseline", gap: 8 }}>
                  <input type="radio" name="into-client" checked={intoOrg === m.org_id}
                         onChange={() => setIntoOrg(m.org_id)} />
                  <span>
                    <b>{m.name}</b>
                    <span className="muted" style={{ fontSize: 12 }}>
                      {" "}&mdash; {m.campaigns} campaign{m.campaigns === 1 ? "" : "s"}
                      {m.created_at ? `, opened ${new Date(m.created_at).toLocaleDateString()}` : ""}
                    </span>
                  </span>
                </label>
              ))}
              <label className="row" style={{ cursor: "pointer", alignItems: "baseline", gap: 8 }}>
                <input type="radio" name="into-client" checked={intoOrg === null}
                       onChange={() => setIntoOrg(null)} />
                <span>Start a separate client with the same name</span>
              </label>
            </div>
          )}

          {/* ── WHAT THIS ACCOUNT CARRIES ─────────────────────────────────────────────────────
              Asked here because it is not a setting the client can change later: a licence is
              either "where their signs may go" or "whose doors they may see", and which one it is
              is the contract. Staff can still change it afterwards from the Account page. */}
          <div>
            <div className="fld" style={{ marginBottom: 6 }}>What this account carries</div>
            <label className="row" style={{ marginBottom: 6, cursor: "pointer", alignItems: "baseline", gap: 8 }}>
              <input type="radio" name="product" checked={!signsOnly} onChange={() => setProduct("full")} />
              <span><b>Full campaign</b>
                <span className="muted" style={{ fontSize: 12 }}> &mdash; voter file, universes, walk turf and surveys for the districts below.</span>
              </span>
            </label>
            <label className="row" style={{ cursor: "pointer", alignItems: "baseline", gap: 8 }}>
              <input type="radio" name="product" checked={signsOnly} onChange={() => setProduct("signs")} />
              <span><b>Signs only</b>
                <span className="muted" style={{ fontSize: 12 }}> &mdash; the yard-sign tools for the districts below and <b>no voter data at all</b>: no voters, no universes, no walkbooks. For a sign vendor.</span>
              </span>
            </label>
          </div>

          {/* BILLING IS A CLIENT FACT, AND THE CLIENT ALREADY EXISTS (2026-09-24). It lives on
              `organization`, and orator_open_account only writes it when the client's is still
              null -- so on this path the field was either a no-op or a back-door way to set a
              client's billing email from a campaign screen, which is the mismatch 0274/0275 exist
              to stop. The Client tab owns it. */}
          {locked ? (
            <label className="fld">Campaign<input className="ctl" value={campaignName}
              onChange={(e) => setCampaignName(e.target.value)} placeholder="HD-122 Race" /></label>
          ) : (
            <div className="grid2">
              <label className="fld">First campaign<input className="ctl" value={campaignName} onChange={(e) => setCampaignName(e.target.value)} placeholder="HD-122 Race" /></label>
              <label className="fld">Billing email<input className="ctl" type="email" value={billing} onChange={(e) => setBilling(e.target.value)} placeholder="bill@acme.com" /></label>
            </div>
          )}
          {/* ── NO ADMIN SECTION WHEN ADDING A CAMPAIGN (Caleb, 2026-09-24) ─────────────────
              "There should be no additional client admin that can be added to the new campaign,
              because that's not where you'd add a client-wide admin. You wouldn't have any of that
              section there. Where you would have it is in your client's interface."

              Right, and the folding I did first was treating the symptom. A client-wide admin is a
              fact about the CLIENT -- it names no campaign and carries every campaign the client
              will ever have -- so a campaign form is not merely a cluttered place to set it, it is
              the wrong place, in exactly the way billing email was. It lives on the client's People
              tab, which is also where you go to take it away. Opening a NEW client still asks,
              because there the account would otherwise have nobody in it at all. */}
          {locked && seated !== false ? (
            <div className="muted" style={{ fontSize: 12 }}>
              Whoever has client-wide access on <b>{orgName}</b> gets this campaign the moment it is
              created — client-wide access names no campaign. Add or withdraw those people on the
              client&rsquo;s <b>People</b> tab.
            </div>
          ) : (<>
            {/* THE ONE CASE WHERE IT STILL BELONGS HERE. A client with NOBODY holding client-wide
                access would get a campaign with nobody in it, and the server refuses that -- so
                hiding the fields here would leave a form that cannot be submitted and nothing on
                screen to fix. The exception proves the rule rather than bending it: the section
                appears exactly when the campaign would otherwise be empty. */}
            {locked && seated === false && (
              <div className="muted" style={{ fontSize: 12, color: "var(--warn,#eab308)" }}>
                <b>{orgName}</b> has nobody with client-wide access, so a campaign added under it
                would have nobody in it. Name an admin here, or grant somebody access on the
                client&rsquo;s People tab first.
              </div>
            )}
            <div className="grid2">
              <label className="fld">
                Client admin email{adminExempt ? "" : " *"}
                <input className="ctl" type="email" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} placeholder="admin@acme.com" />
              </label>
              <label className="fld">Admin name<input className="ctl" value={adminName} onChange={(e) => setAdminName(e.target.value)} placeholder="Jane Smith" /></label>
            </div>
            <div className="muted" style={{ fontSize: 12, marginTop: -4 }}>
              The admin gets client-wide access: every campaign under this client, including ones
              opened later. They are <b>emailed a link and set their own password</b> — nobody here
              types a credential for them. If the address already has an Orator login, that login is
              used and their existing password still works.
            </div>
          </>)}
          {locked && seated === "error" && (
            <div className="muted" style={{ fontSize: 12, color: "var(--warn,#eab308)" }}>
              Couldn&rsquo;t check who this client already has. That is not an answer either way, so
              it is not blocking you — but if the client turns out to have nobody, opening will be
              refused and will say so.
            </div>
          )}

          <div className="acct-sec">
            <div className="fld" style={{ marginBottom: 6 }}>Districts (access)</div>
            {scopes.map((sc, i) => (
              <div key={i} style={{ marginBottom: 8, display: "grid", gap: 6 }}>
                <div className="row">
                  <DistrictPicker state={state} scopeType={sc.scope_type} scopeValue={sc.scope_value}
                    scopeFips={sc.scope_fips} scopePlace={sc.scope_place}
                    onType={(t) => setScope(i, "scope_type", t)} onValue={(v) => setScope(i, "scope_value", v)}
                    onFips={(f) => setScope(i, "scope_fips", f)} onPlace={(pl) => setScope(i, "scope_place", pl)} />
                  <button className="btn sm" onClick={() => setScopes((p) => p.filter((_, j) => j !== i))}>✕</button>
                </div>
                {PLACE_TYPES.has(sc.scope_type) && <CityFacts state={state} place={sc.scope_place}
                  scopeType={sc.scope_type} district={sc.scope_value} />}
              </div>
            ))}
            <button className="btn sm" onClick={() => setScopes((p) => [...p, { scope_type: "state_house", scope_value: "", scope_fips: null, scope_place: null }])}>＋ Add district</button>
          </div>
          <div className="acct-sec">
            <div className="fld" style={{ marginBottom: 6 }}>Volunteer prefixes — at least one required</div>
            {/* THIS ONE STAYS, AND IT IS THE ONE THAT LOOKED MOST SKIPPABLE. A prefix belongs to
                exactly ONE campaign -- orator_grant_prefix refuses one another campaign already
                holds -- so a second campaign under a client genuinely needs its own. What was
                missing was any sight of what the client already uses, which left staff inventing a
                name next to names they could not see (2026-09-24). */}
            {/* A PREFIX IS MARRIED TO THE CAMPAIGN, NOT THE CLIENT (Caleb, 2026-09-24). A previous
                draft of this screen listed the prefixes the client's OTHER campaigns hold, on the
                theory that it would help pick a consistent one. It does the opposite: it answers a
                question nobody asked, with another campaign's name, directly above the box whose
                own live verdict is the answer that matters. "It shouldn't be showing me another
                prefix for the client, because the prefixes are married to the campaign."
                The check below is the feature -- type, and it says Available or Unavailable. */}
            {locked ? (
              <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
                This campaign needs its <b>own</b> — a prefix belongs to one campaign and cannot be
                shared with another, even under the same client. Start typing and Orator says
                whether the name is free. 2–15 lowercase letters, no digits; the number on the end
                is what Orator adds.
              </div>
            ) : (
              <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
                The name this client's quick volunteers sign in under — a prefix of <b>acmepac</b> gives
                them <b>acmepac1</b>, <b>acmepac2</b>, and so on. Claimed across the whole platform and
                never reusable once accounts exist under it, so pick something that won't collide with
                another client. The account can't be opened without one: their managers create
                volunteers by picking from what you assign here. 2–15 lowercase letters, no digits —
                the number on the end is what Orator adds.
              </div>
            )}
            {pfxs.map((p, i) => {
              const key = p.trim().toLowerCase();
              const c = key ? pfxChecks[key] : undefined;
              return (
                <div key={i} style={{ marginBottom: 8 }}>
                  <div className="row">
                    <input className="ctl" value={p} placeholder="clientshorthand"
                      autoCapitalize="none" autoCorrect="off" spellCheck={false}
                      onChange={(e) => setPfxs((x) => x.map((v, j) => (j === i ? e.target.value : v)))} />
                    <button className="btn sm" onClick={() => {
                      setPfxs((x) => (x.length === 1 ? [""] : x.filter((_, j) => j !== i)));
                    }}>✕</button>
                  </div>
                  {/* ONE VERDICT, THEN THE REASON. Caleb: "there should basically be like
                      available and unavailable ... really in this moment I need to know is it
                      available or not." So the first word is the answer and everything else is the
                      short phrase after it -- but "why not" stays, because staff are choosing a
                      name they cannot take back once it has accounts under it.

                      A HOLDER IS ALWAYS NAMED, and a closed account is still a holder. Caleb:
                      "even though the campaign is under a closed account, the campaign is still
                      what's holding the prefix and it's designed to stay that way." It is --
                      orator_close_account sets campaign.status='closed' and never deletes the
                      campaign, so the row survives and this names it. I had claimed a cascade
                      here without checking, which is where the previous draft's login-count
                      wording came from; the count was always the least useful half of the answer.
                      The no-holder branch below is now a belt-and-braces fallback that only a
                      direct DELETE could produce, kept because granting a name whose handles
                      already exist half-fails at a table -- Supabase refuses the duplicate login
                      and the batch comes up short with twenty volunteers waiting.

                      AND A FAILED CHECK IS AN ERROR, NOT A VERDICT. It says nothing about the
                      name; it says the request did not come back. Opening stays blocked either
                      way, but calling it "unavailable" would send staff off to invent a second
                      name for a network blip. */}
                  <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                    {!key ? "2–15 lowercase letters, no digits."
                     : c === undefined || c === "checking" ? "Checking…"
                     : c === "error"
                       ? <span style={{ color: "var(--warn,#eab308)" }}>
                           <b>Error</b> — couldn't reach the server. Try again.
                         </span>
                     : c.available
                       ? <span style={{ color: "var(--good,#34d399)" }}>
                           <b>Available</b> — they'll be {key}1, {key}2, …
                         </span>
                       : <span style={{ color: "var(--bad,#f87171)" }}>
                           <b>Unavailable</b> — {
                             !c.shape_ok ? "invalid — use 2–15 lowercase letters, no digits"
                             : c.holder ? `held by ${c.holder}`
                             : "volunteer logins already exist under this name"}
                         </span>}
                  </div>
                </div>
              );
            })}
            <button className="btn sm" onClick={() => setPfxs((x) => [...x, ""])}>＋ Add prefix</button>
          </div>
          {err && <div className="err">{err}</div>}
        </div>
        <div className="acct-bar">
          <button className="btn" onClick={onClose}>Cancel</button>
          <span className="muted" style={{ fontSize: 13 }}>Nothing is saved until you {intoOrg ? "add the campaign" : "open the account"}.</span>
          <button className="btn accent" style={{ marginLeft: "auto" }} disabled={busy || !valid} onClick={submit}>{busy ? "Opening…" : intoOrg ? "Add campaign" : "Open account"}</button>
        </div>
    </div>
  );
}

// 0264 — the client page, in tabs. What used to be here was one flat form with seven fields, of
// which the last was an "Admin email (login)" that was greyed out unless an admin already existed:
// a password field wearing a business label. A client is a relationship — where the invoice goes,
// who signs, what we agreed to sell them, whose affidavit covers their data — and none of that fits
// on one screen or belongs to a campaign.
//
// Five tabs, because these are five different conversations, not one long form. "Campaign" is last
// on purpose: a client may carry several (0263), and this modal is opened from one campaign's row.
type ClientRec = {
  org_id: string; name: string; status: string | null; client_type: string | null;
  bill_to_name: string | null; addr_line1: string | null; addr_line2: string | null;
  addr_city: string | null; addr_state: string | null; addr_postal: string | null;
  billing_email: string | null; billing_status: string; payment_terms: string | null;
  tax_treatment: string | null;
  // The deal (0268). Two shapes: one price covering everything for a term, or a rate per campaign.
  agreement_type: string | null; agreement_price: number | null; agreement_period: string | null;
  agreement_state: string | null;
  term_start: string | null; term_end: string | null;
  notes: string | null; campaigns: number; created_at: string | null;
};
type ClientContact = {
  id: string; campaign_id: string | null; campaign_name: string | null; name: string;
  email: string | null; phone: string | null; for_invoices: boolean; for_contracts: boolean;
  for_daily: boolean; app_user_id: string | null; note: string | null;
  /** Archived rather than deleted since 0310 — "who did we used to invoice" is a question this
   *  screen exists to answer. */
  archived_at?: string | null; archived_by_name?: string | null;
};
const CLIENT_TABS = [
  { k: "client",   label: "Client" },
  { k: "billing",  label: "Billing" },
  // ONE TAB, BECAUSE IT IS ONE QUESTION (Caleb, 2026-09-24: "People and Access shouldn't be more
  // or less tied together, where you're looking at all the users and you're looking at the level of
  // access that they have ... their designations").
  //
  // They were split because they ARE different facts -- a contact is who you invoice, access is who
  // can open the campaigns -- and 0273's note above this line said so firmly. That is still true of
  // the facts and was never true of the PEOPLE: somebody who is both appeared twice, on two tabs,
  // with neither row mentioning the other. The model knew: client_contact.app_user_id has carried
  // "this contact also happens to have a login" since 0264 and nothing had ever joined on it.
  { k: "people",   label: "People" },
  { k: "campaign", label: "This campaign" },
] as const;

type ClientManager = { app_user_id: string; display_name: string | null; login_id: string;
                       role: string; is_active: boolean; since: string | null };

/** The client record, its billing, its people — and, when opened from a campaign, that campaign.
 *
 *  `scope` decides what this screen is ABOUT. Opened from a client it shows the client and nothing
 *  campaign-shaped; opened from a campaign it starts on the campaign. It used to be campaign-only
 *  and open on the client tab, which is how a client ended up with no way to edit it and a campaign
 *  ended up opening on fields that belong to something else.
 *
 *  A campaign keeps its client tabs on purpose — while you are looking at a race, the client's
 *  billing is one click away and that is useful. What changed is where it STARTS. */
function EditModal({ account, scope = "campaign", onClose, onDone }: {
  account: Account; scope?: "client" | "campaign"; onClose: () => void; onDone: () => void;
}) {
  const clientOnly = scope === "client";
  // The counties this agreement pays for, and every county in the state to pick from. Loaded only
  // when a county-wide agreement is on screen — 254 rows is not worth fetching to look at a price.
  const [agCounties, setAgCounties] = useState<{ fips: number; county: string | null }[] | null>(null);
  const [allCounties, setAllCounties] = useState<{ fips: number; county: string }[] | null>(null);
  const [pickCounty, setPickCounty] = useState("");
  // DID THE READ SUCCEED, separately from what it returned. An empty county dropdown must mean
  // "this state has no counties", never "the request failed" — and an empty agreement list must
  // mean "they bought nothing", never "we could not ask". Same distinction the accounts list keeps
  // with accountsOk/findingsOk, and the reason empty-vs-error-guard exists.
  const [agCountiesErr, setAgCountiesErr] = useState<string | null>(null);
  const [countiesErr, setCountiesErr] = useState<string | null>(null);
  const [managers, setManagers] = useState<ClientManager[] | null>(null);
  const [managersErr, setManagersErr] = useState<string | null>(null);
  const [newManager, setNewManager] = useState("");
  // THE ADDRESS THE SERVER SAID HAS NO LOGIN, held so the screen can offer the second act. Null
  // means no such offer is open; this is never set from a guess, only from the server's 404.
  const [inviteFor, setInviteFor] = useState<string | null>(null);
  const [inviteName, setInviteName] = useState("");
  // IS THIS CAMPAIGN INSIDE WHAT THEY ALREADY PAY FOR (0272). Read, never computed here: a district
  // crosses county lines and only the database knows which counties it reaches.
  const [coverage, setCoverage] = useState<{ status: string; reason: string } | null>(null);
  const tabs = clientOnly ? CLIENT_TABS.filter((t) => t.k !== "campaign") : CLIENT_TABS;
  const [tab, setTab] = useState<string>(clientOnly ? "client" : "campaign");
  const [rec, setRec] = useState<ClientRec | null>(null);
  const [contacts, setContacts] = useState<ClientContact[] | null>(null);
  // Active / archived / all, over BOTH halves of the People tab at once -- which is the whole point
  // of them meaning the same thing by "not current" (0310).
  const [pstatus, setPstatus] = useState<"active" | "archived" | "all">("active");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  // Campaign-level fields stay campaign-level. They were always the minority of this form.
  const [campaignName, setCampaignName] = useState(account.campaign_name || "");
  const [state, setState] = useState(account.state || "TX");
  const [status, setStatus] = useState(account.status || "active");
  // THE ACCOUNT HOLDER, NOT A CAMPAIGN ROLE (0275). These used to be "Lead manager name/login" on
  // the campaign tab, saved through a campaign-scoped call that reached a person via
  // campaign.owner_user_id. That pointer is retired — on production it named owner@demo.org for a
  // client whose holder was Caleb — so they are keyed by the CLIENT and live on the Client tab.
  const [holderName, setHolderName] = useState(account.holder || "");
  const [holderLogin, setHolderLogin] = useState("");
  const [rate, setRate] = useState(account.rate != null ? String(account.rate) : "");
  const [ratePeriod, setRatePeriod] = useState(account.rate_period || "per_cycle");
  // WHAT THIS ACCOUNT CARRIES (0307). Undefined is the historical account: voter data, no signs.
  const [hasVoters, setHasVoters] = useState(account.voters_enabled !== false);
  const [hasSigns, setHasSigns] = useState(!!account.places_enabled);

  const orgId = account.org_id;

  async function load() {
    if (!orgId) { setErr("This campaign has no client attached, so there is no client record to edit."); return; }
    setErr(null);
    try {
      const c = await api<{ client: ClientRec }>(`/portal/client?org_id=${encodeURIComponent(orgId)}`);
      setRec(c.client);
      const k = await api<{ contacts: ClientContact[] }>(
        `/portal/client/contacts?org_id=${encodeURIComponent(orgId)}&status=${pstatus}`);
      setContacts(k.contacts);
      // Non-fatal, like every other secondary read on this screen: a county list that will not load
      // must not blank the client record behind it.
      try {
        const ac = await api<{ counties: { fips: number; county: string | null }[] }>(
          `/portal/client/agreement-counties?org_id=${encodeURIComponent(orgId)}&state=${encodeURIComponent(account.state || "TX")}`);
        setAgCounties(ac.counties || []); setAgCountiesErr(null);
      } catch (e: any) { setAgCountiesErr(String(e?.message || e)); }
      if (!clientOnly) {
        try {
          const cv = await api<{ status: string; reason: string }>(
            `/portal/campaign/coverage?campaign_id=${encodeURIComponent(account.campaign_id)}`);
          setCoverage(cv);
        } catch { setCoverage(null); }
      }
    } catch (e: any) {
      // Never an empty form on a failed read: a blank field here reads as "we know nothing about
      // this client", and saving over it would make that true.
      setErr(String(e.message || e));
    }
  }
  useEffect(() => { load(); }, [orgId, pstatus]);   // the toggle re-reads; archived is a query, not a filter

  function set<K extends keyof ClientRec>(k: K, v: ClientRec[K]) {
    setRec((r) => (r ? { ...r, [k]: v } : r));
  }

  // Client-wide access, loaded when the tab is opened. Its own read so a failure here cannot take
  // the client record with it — and, per empty-vs-error, an empty list must mean nobody holds it
  // rather than "we could not ask".
  useEffect(() => {
    if (tab !== "people" || !orgId || managers !== null || managersErr) return;
    api<{ managers: ClientManager[] }>(`/portal/client/managers?org_id=${encodeURIComponent(orgId)}`)
      .then((r) => { setManagers(r.managers || []); setManagersErr(null); })
      .catch((e: any) => setManagersErr(String(e?.message || e)));
  }, [tab, orgId, managers, managersErr]);

  async function setManager(login: string, active: boolean, invite = false) {
    if (!orgId || !login.trim()) return;
    setBusy(true); setErr(null); setSaved(null);
    try {
      await api("/portal/client/manager", { method: "POST", body: JSON.stringify({
        org_id: orgId, login_id: login.trim(), active, invite,
        display_name: invite ? (inviteName.trim() || null) : null }) });
      setManagers(null); setManagersErr(null);   // re-read rather than patch: the server decides
      setNewManager(""); setInviteFor(null); setInviteName("");
      setSaved(invite
        ? `Invited ${login.trim()} — they'll get an email to set their own password, and they have client-wide access as soon as they do.`
        : active ? "Client-wide access granted." : "Client-wide access withdrawn.");
    } catch (e: any) {
      // 404 HERE MEANS ONE SPECIFIC THING: that address has no Orator login yet. It is the only
      // failure with an obvious next move, so it becomes an offer rather than a red line. Every
      // other failure is still just reported — guessing at "maybe they need inviting" on a 500
      // would put staff one click from creating an account nobody asked for.
      if (e instanceof ApiError && e.status === 404 && active && !invite) {
        setInviteFor(login.trim()); setInviteName("");
      } else { setErr(String(e.message || e)); }
    }
    finally { setBusy(false); }
  }

  // The full county list is only needed once a county-wide agreement is being edited.
  useEffect(() => {
    if (rec?.agreement_type !== "countywide" || allCounties !== null) return;
    api<{ counties: { fips: number; county: string }[] }>(
      `/portal/counties?state=${encodeURIComponent(account.state || "TX")}`)
      .then((r) => { setAllCounties(r.counties || []); setCountiesErr(null); })
      .catch((e: any) => setCountiesErr(String(e?.message || e)));
  }, [rec?.agreement_type, allCounties, account.state]);

  async function addCounty() {
    const f = Number(pickCounty);
    const c = (allCounties || []).find((x) => x.fips === f);
    if (!orgId || !f) return;
    setBusy(true); setErr(null);
    try {
      await api("/portal/client/agreement-county", { method: "POST", body: JSON.stringify({
        org_id: orgId, state: account.state || "TX", fips: f, county: c?.county ?? null }) });
      setAgCounties([...(agCounties || []), { fips: f, county: c?.county ?? null }]
        .sort((a, b) => (a.county || "").localeCompare(b.county || "")));
      setPickCounty("");
    } catch (e: any) { setErr(String(e.message || e)); }
    finally { setBusy(false); }
  }

  async function removeCounty(fips: number) {
    if (!orgId) return;
    setBusy(true); setErr(null);
    try {
      await api("/portal/client/agreement-county/remove", { method: "POST", body: JSON.stringify({
        org_id: orgId, state: account.state || "TX", fips }) });
      setAgCounties((agCounties || []).filter((c) => c.fips !== fips));
    } catch (e: any) { setErr(String(e.message || e)); }
    finally { setBusy(false); }
  }

  async function saveClient() {
    if (!rec || !orgId) return;
    setBusy(true); setErr(null); setSaved(null);
    try {
      await api("/portal/client/update", {
        method: "POST",
        body: JSON.stringify({
          org_id: orgId, name: rec.name, client_type: rec.client_type ?? "",
          bill_to_name: rec.bill_to_name ?? "", addr_line1: rec.addr_line1 ?? "",
          addr_line2: rec.addr_line2 ?? "", addr_city: rec.addr_city ?? "",
          addr_state: rec.addr_state ?? "", addr_postal: rec.addr_postal ?? "",
          billing_email: rec.billing_email ?? "", billing_status: rec.billing_status,
          payment_terms: rec.payment_terms ?? "", tax_treatment: rec.tax_treatment ?? "",
          agreement_type: rec.agreement_type ?? "", agreement_period: rec.agreement_period ?? "",
          agreement_state: rec.agreement_state ?? "",
          agreement_price: rec.agreement_price, clear_price: rec.agreement_price == null,
          term_start: rec.term_start ?? null, term_end: rec.term_end ?? null,
          notes: rec.notes ?? "",
        }),
      });
      // A SEPARATE CALL, ON PURPOSE. Renaming a person is not editing a client record, and
      // orator_set_holder_identity rewrites a LOGIN — the thing somebody signs in with. Folding it
      // into the client save would mean every "Save client" press carried it, including the ones
      // where the two fields were merely left as they were found.
      const hn = holderName.trim(), hl = holderLogin.trim();
      if (account.holder_user_id && (hl || (hn && hn !== (account.holder || "")))) {
        await api("/portal/client/holder", { method: "POST", body: JSON.stringify({
          org_id: orgId, name: hn || null, login: hl || null }) });
        setHolderLogin("");
      }
      setSaved("Client saved."); await load();
    } catch (e: any) { setErr(String(e.message || e)); }
    finally { setBusy(false); }
  }

  async function saveCampaign() {
    setBusy(true); setErr(null); setSaved(null);
    try {
      await api("/portal/account/update", {
        method: "POST",
        body: JSON.stringify({
          campaign_id: account.campaign_id,
          campaign_name: campaignName.trim() || null,
          state: state.trim() || null,
          status: status || null,
        }),
      });
      // WHAT THE ACCOUNT CARRIES IS ITS OWN CALL, for the same reason the rate is: turning the
      // voter file on is a sale, and it must not ride along on a press that only renamed a race.
      // Sent only when it actually changed.
      if (hasVoters !== (account.voters_enabled !== false) || hasSigns !== !!account.places_enabled) {
        await api("/portal/account/products", { method: "POST", body: JSON.stringify({
          campaign_id: account.campaign_id, voters_enabled: hasVoters, places_enabled: hasSigns }) });
      }
      // The rate is its own call: a price that can be changed by the screen that renames a
      // campaign is a price somebody changes by accident, so the backend keeps them apart.
      if (rec?.agreement_type !== "statewide") {
        const r = rate.trim();
        await api("/portal/campaign/rate", { method: "POST", body: JSON.stringify({
          campaign_id: account.campaign_id,
          rate: r === "" ? null : Number(r), rate_period: ratePeriod, clear: r === "" }) });
      }
      setSaved("Campaign saved."); onDone();
    } catch (e: any) { setErr(String(e.message || e)); setBusy(false); }
  }

  // NAME THE THING BEING EDITED. This read the client's name whichever row you came from, so a
  // campaign's Edit was headed with somebody else's name — the same confusion as opening on the
  // client tab, one line higher up.
  // An agreement that covers a territory: both of these have a price and a place, and both want a
  // state. `per_campaign` has neither — the price lives on each campaign.
  const hasTerritory = rec?.agreement_type === "statewide" || rec?.agreement_type === "countywide";

  const title = clientOnly ? (rec?.name || account.org_name || "Client")
                           : account.campaign_name;
  // On a campaign, say which client it sits under — the fact you most want while editing a race,
  // and the one the old header accidentally replaced the campaign's own name with.
  const subtitle = clientOnly ? null : (rec?.name || account.org_name || "no client");

  return (
    <div className="overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 760 }} onClick={(e) => e.stopPropagation()}>
        <div className="head">
          {title}
          {subtitle && <span className="muted" style={{ marginLeft: 8, fontWeight: 400, fontSize: 13 }}>· {subtitle}</span>}
          {clientOnly && rec && <span className="chip" style={{ marginLeft: 8, fontSize: 11 }}>{rec.campaigns} campaign{rec.campaigns === 1 ? "" : "s"}</span>}
          <button className="btn sm" style={{ marginLeft: "auto" }} onClick={onClose}>✕</button>
        </div>

        <div className="row" style={{ gap: 4, padding: "10px 16px 0", flexWrap: "wrap" }}>
          {tabs.map((t) => (
            <button key={t.k} className={"btn sm" + (tab === t.k ? " accent" : "")}
                    onClick={() => { setTab(t.k); setSaved(null); }}>{t.label}</button>
          ))}
        </div>

        <div className="body" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {err && <div className="err">{err}</div>}
          {saved && <div className="muted" style={{ fontSize: 13 }}>{saved}</div>}
          {!rec && !err && <div className="muted">Loading the client record…</div>}

          {rec && tab === "client" && (<>
            <div className="grid2">
              <label className="fld">Client name<input className="ctl" value={rec.name} onChange={(e) => set("name", e.target.value)} /></label>
              <label className="fld">Type
                <select className="ctl" value={rec.client_type || ""} onChange={(e) => set("client_type", e.target.value || null)}>
                  <option value="">Not set</option>
                  <option value="firm">Consulting firm</option>
                  <option value="campaign">Candidate campaign</option>
                  <option value="party">Party committee</option>
                  <option value="officeholder">Officeholder</option>
                  <option value="other">Other</option>
                </select>
              </label>
            </div>
            <label className="fld">Bill-to name
              <input className="ctl" value={rec.bill_to_name || ""} placeholder="Only if the invoice needs a different name"
                     onChange={(e) => set("bill_to_name", e.target.value)} /></label>
            {/* ── THE ACCOUNT HOLDER ────────────────────────────────────────────────────────
                The billing principal, and the only person on the client side who can hand out
                account-wide access (0274). They can SEE who it is on their own Account page;
                correcting the name or the login is identity work and stays with Orator. */}
            <div className="grid2">
              <label className="fld">Account holder name
                <input className="ctl" value={holderName} onChange={(e) => setHolderName(e.target.value)}
                       placeholder={account.holder_user_id ? "Jane Smith" : "no account holder yet"}
                       disabled={!account.holder_user_id} /></label>
              <label className="fld">Account holder login
                <input className="ctl" type="email" value={holderLogin} onChange={(e) => setHolderLogin(e.target.value)}
                       placeholder={account.holder_user_id ? "leave blank to keep current login" : "no account holder yet"}
                       disabled={!account.holder_user_id} /></label>
            </div>
            <div className="muted" style={{ fontSize: 12, marginTop: -4 }}>
              {account.holder_user_id
                ? <>Currently <b>{account.holder || "unnamed"}</b>. Changing WHO the holder is isn&rsquo;t done here — it follows the contract.</>
                : <>Nobody holds this account. Open a campaign under it with an admin email, or grant client-wide access and set the holder in SQL.</>}
            </div>
            <label className="fld">Address<input className="ctl" value={rec.addr_line1 || ""} onChange={(e) => set("addr_line1", e.target.value)} placeholder="Street" /></label>
            <input className="ctl" value={rec.addr_line2 || ""} onChange={(e) => set("addr_line2", e.target.value)} placeholder="Suite, floor (optional)" aria-label="Address line 2" />
            <div className="grid2">
              <label className="fld">City<input className="ctl" value={rec.addr_city || ""} onChange={(e) => set("addr_city", e.target.value)} /></label>
              <div className="grid2">
                <label className="fld">State<input className="ctl" maxLength={2} value={rec.addr_state || ""} onChange={(e) => set("addr_state", e.target.value.toUpperCase())} /></label>
                <label className="fld">ZIP<input className="ctl" value={rec.addr_postal || ""} onChange={(e) => set("addr_postal", e.target.value)} /></label>
              </div>
            </div>
            <label className="fld">Notes<textarea className="ctl" rows={3} value={rec.notes || ""} onChange={(e) => set("notes", e.target.value)} /></label>
          </>)}

          {rec && tab === "billing" && (<>
            <div className="grid2">
              <label className="fld">Billing email<input className="ctl" type="email" value={rec.billing_email || ""} onChange={(e) => set("billing_email", e.target.value)} /></label>
              <label className="fld">Billing status
                <select className="ctl" value={rec.billing_status} onChange={(e) => set("billing_status", e.target.value)}>
                  <option value="unbilled">Not billed</option>
                  <option value="trial">Trial</option>
                  <option value="comped">Comped</option>
                  <option value="billed">Billed</option>
                </select>
              </label>
            </div>
            <div className="muted" style={{ fontSize: 12, marginTop: -6 }}>
              “Not billed” is a decision. It is what separates an account we are giving away from one
              somebody forgot to finish setting up.
            </div>
            <div className="grid2">
              <label className="fld">Payment terms<input className="ctl" value={rec.payment_terms || ""} placeholder="Net 30, prepay…" onChange={(e) => set("payment_terms", e.target.value)} /></label>
              <label className="fld">Agreement
                <select className="ctl" value={rec.agreement_type || ""}
                        onChange={(e) => set("agreement_type", e.target.value || null)}>
                  <option value="">Not set</option>
                  <option value="per_campaign">A rate per campaign</option>
                  <option value="statewide">One price — a whole state</option>
                  <option value="countywide">One price — named counties</option>
                </select>
              </label>
            </div>
            {/* A PRICE WITH NO TERRITORY CANNOT BE CHECKED AGAINST ANYTHING (0272). "Statewide"
                said how much and never said where, so nothing could answer whether a new race was
                inside what the client already pays for. */}
            {hasTerritory && (
              <div className="grid2">
                <label className="fld">State
                  <input className="ctl" maxLength={2} placeholder="TX"
                         value={rec.agreement_state || ""}
                         onChange={(e) => set("agreement_state", e.target.value.toUpperCase())} />
                </label>
                <div />
              </div>
            )}
            {hasTerritory && (
              <div className="grid2">
                <label className="fld">Price
                  <input className="ctl" type="number" min={0} step="0.01"
                         value={rec.agreement_price ?? ""}
                         onChange={(e) => set("agreement_price", e.target.value === "" ? null : Number(e.target.value))} />
                </label>
                <label className="fld">Per
                  <select className="ctl" value={rec.agreement_period || ""}
                          onChange={(e) => set("agreement_period", e.target.value || null)}>
                    <option value="">Not set</option>
                    <option value="monthly">Month</option>
                    <option value="annual">Year</option>
                    <option value="per_cycle">Cycle</option>
                    <option value="one_time">One time</option>
                  </select>
                </label>
              </div>
            )}
            {rec.agreement_type === "per_campaign" && (
              <div className="muted" style={{ fontSize: 12, marginTop: -6 }}>
                Each campaign carries its own rate, set on its own tab — different territories,
                different numbers.
              </div>
            )}
            {rec.agreement_type === "countywide" && (
              <div style={{ marginTop: 4 }}>
                <div className="fld" style={{ marginBottom: 6 }}>Counties this agreement pays for</div>
                <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
                  A campaign is checked against this list. A district that reaches into a county
                  that is not here counts as outside the agreement.
                </div>
                <div className="row" style={{ gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
                  {agCountiesErr ? <span className="muted" style={{ color: "var(--warn,#eab308)" }}>
                       Couldn&rsquo;t load this agreement&rsquo;s counties — this is not an empty
                       agreement. {agCountiesErr}
                     </span>
                   : agCounties === null ? <span className="muted">Loading…</span>
                   : agCounties.length === 0 ? <span className="muted">No counties yet — the agreement covers nothing.</span>
                   : agCounties.map((c) => (
                      <span className="chip" key={c.fips}>
                        {c.county || c.fips}
                        <button className="btn sm" style={{ marginLeft: 6 }} disabled={busy}
                                onClick={() => removeCounty(c.fips)} aria-label={`Remove ${c.county || c.fips}`}>×</button>
                      </span>
                   ))}
                </div>
                {countiesErr ? (
                  <div className="muted" style={{ fontSize: 13, color: "var(--warn,#eab308)" }}>
                    Couldn&rsquo;t load the county list, so there is nothing to choose from — this
                    state has not run out of counties. {countiesErr}
                  </div>
                ) : (
                  <div className="row" style={{ gap: 8 }}>
                    <select className="ctl" style={{ maxWidth: 260 }} value={pickCounty} disabled={allCounties === null}
                            onChange={(e) => setPickCounty(e.target.value)} aria-label="County to add">
                      <option value="">{allCounties === null ? "Loading counties…" : "— pick a county —"}</option>
                      {(allCounties || []).filter((c) => !(agCounties || []).some((a) => a.fips === c.fips))
                        .map((c) => <option key={c.fips} value={String(c.fips)}>{c.county}</option>)}
                    </select>
                    <button className="btn" disabled={!pickCounty || busy} onClick={addCounty}>Add</button>
                  </div>
                )}
              </div>
            )}
            <div className="grid2">
              <label className="fld">Term starts
                <input className="ctl" type="date" value={rec.term_start || ""}
                       onChange={(e) => set("term_start", e.target.value || null)} /></label>
              <label className="fld">Term ends
                <input className="ctl" type="date" value={rec.term_end || ""}
                       onChange={(e) => set("term_end", e.target.value || null)} /></label>
            </div>
            <label className="fld">Sales tax
              <select className="ctl" value={rec.tax_treatment || ""} onChange={(e) => set("tax_treatment", e.target.value || null)}>
                <option value="">Not set</option>
                <option value="taxable">Taxable</option>
                <option value="exempt_certificate">Exempt — certificate on file</option>
                <option value="not_applicable">Not applicable</option>
              </select>
            </label>
            <div className="muted" style={{ fontSize: 12, marginTop: -6 }}>
              Texas taxes 80% of the charge for software access and for data sold as a product.
              Consulting is argued separately, which only works if the invoice already separates it.
            </div>
          </>)}

          {rec && tab === "people" && (
            <PeoplePanel orgId={rec.org_id} campaignId={account.campaign_id}
                         campaignName={account.campaign_name}
                         contacts={contacts} onChanged={load}
                         pstatus={pstatus} onStatus={setPstatus}
                         managers={managers} managersErr={managersErr}
                         busy={busy} onSetManager={setManager}
                         newManager={newManager} setNewManager={setNewManager}
                         inviteFor={inviteFor} setInviteFor={setInviteFor}
                         inviteName={inviteName} setInviteName={setInviteName} />
          )}

          {tab === "campaign" && (<>
            <div className="grid2">
              <label className="fld">Campaign<input className="ctl" value={campaignName} onChange={(e) => setCampaignName(e.target.value)} /></label>
              <label className="fld">State<input className="ctl" maxLength={2} value={state} onChange={(e) => setState(e.target.value.toUpperCase())} /></label>
            </div>
            <div className="grid2">
              <label className="fld">Status
                <select className="ctl" value={status} onChange={(e) => setStatus(e.target.value)}>
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                  <option value="closed">Closed</option>
                </select>
              </label>
            </div>
            {/* ── WHAT THIS ACCOUNT CARRIES ───────────────────────────────────────────────
                The licence rows under “Manage · Licence” say WHERE. These two say what that
                licence means: voter data, the sign programme, or both. A signs-only account is
                how a sign vendor buys Orator without buying the voter file (0307). */}
            <div>
              <div className="fld" style={{ marginBottom: 6 }}>What this account carries</div>
              <label className="row" style={{ marginBottom: 4, cursor: "pointer", alignItems: "baseline", gap: 8 }}>
                <input type="checkbox" checked={hasVoters} onChange={(e) => setHasVoters(e.target.checked)} />
                <span>Voter file
                  <span className="muted" style={{ fontSize: 12 }}> &mdash; voters, universes, walk turf and surveys inside the licensed districts. Off means the licence covers signs only and no voter row is reachable at all.</span>
                </span>
              </label>
              <label className="row" style={{ cursor: "pointer", alignItems: "baseline", gap: 8 }}>
                <input type="checkbox" checked={hasSigns} onChange={(e) => setHasSigns(e.target.checked)} />
                <span>Sign programme
                  <span className="muted" style={{ fontSize: 12 }}> &mdash; yard and highway signs, spots and sign books, on the dashboard and in the field app.</span>
                </span>
              </label>
              {!hasVoters && !hasSigns && (
                <div className="muted" style={{ fontSize: 12, marginTop: 6, color: "var(--warn,#eab308)" }}>
                  An account with neither has nothing in it. Set the status to Closed instead.
                </div>
              )}
            </div>
            {/* THE LEAD MANAGER FIELDS WERE HERE UNTIL 0275. They named a person through
                campaign.owner_user_id and saved through a campaign-scoped call — a client fact
                edited from a campaign screen. They are now "Account holder" on the Client tab. */}
            {/* COVERED, OUTSIDE, OR NOT KNOWN YET — from the database, not guessed from the
                agreement's type. "statewide" used to imply included, which is true of a state the
                campaign is actually IN; a county-wide agreement cannot be answered at all without
                knowing which counties the campaign's districts reach. See 0272.
                `unknown` deliberately still offers the rate field: refusing to price something we
                merely cannot classify would be the same confident silence, pointing the other way. */}
            {coverage && coverage.status === "covered" ? (
              <div className="muted" style={{ fontSize: 13 }}>
                <b>Included</b> — {coverage.reason} This campaign carries no separate rate.
              </div>
            ) : (<>
              {coverage && coverage.status === "outside" && (
                <div className="muted" style={{ fontSize: 13, color: "var(--warn,#eab308)" }}>
                  <b>Outside the agreement</b> — {coverage.reason} Bill it separately, or widen the
                  agreement on the Billing tab.
                </div>
              )}
              {coverage && coverage.status === "unknown" && (
                <div className="muted" style={{ fontSize: 13 }}>
                  <b>Not known yet</b> — {coverage.reason}
                </div>
              )}
              <div className="grid2">
                <label className="fld">Rate for this campaign
                  <input className="ctl" type="number" min={0} step="0.01" value={rate}
                         placeholder="leave blank for none"
                         onChange={(e) => setRate(e.target.value)} /></label>
                <label className="fld">Per
                  <select className="ctl" value={ratePeriod} onChange={(e) => setRatePeriod(e.target.value)}>
                    <option value="monthly">Month</option>
                    <option value="annual">Year</option>
                    <option value="per_cycle">Cycle</option>
                    <option value="one_time">One time</option>
                  </select>
                </label>
              </div>
            </>)}
            <div className="muted" style={{ fontSize: 12, marginTop: -4 }}>
              This is a sign-in, not a business contact — the people who get invoices and sign
              contracts live under People. Changing it updates their Orator login id only; the
              Supabase Auth sign-in email is managed separately. Licensed turf lives under “Manage · Licence”.
            </div>
          </>)}
        </div>

        <div className="foot">
          <button className="btn" onClick={onClose}>Close</button>
          {tab === "campaign"
            ? <button className="btn accent" style={{ marginLeft: "auto" }} disabled={busy || !campaignName.trim() || !state.trim() || (!hasVoters && !hasSigns)} onClick={saveCampaign}>{busy ? "Saving…" : "Save campaign"}</button>
            : tab === "people"
              ? null
              : <button className="btn accent" style={{ marginLeft: "auto" }} disabled={busy || !rec || !rec.name.trim()} onClick={saveClient}>{busy ? "Saving…" : "Save client"}</button>}
        </div>
      </div>
    </div>
  );
}

// The people at a client. Three jobs as three ticks rather than three rows, because one person
// usually does all three and the form should let you say so by ticking boxes.
/** THE PEOPLE ON A CLIENT, IN ONE LIST (0310).
 *
 *  Caleb: "People and Access shouldn't be more or less tied together, where you're looking at all
 *  the users and you're looking at the level of access that they have ... and in the same way that
 *  we have Active and Archived, that's what we should have here. I shouldn't have a list that just
 *  has it greyed out."
 *
 *  Two facts about a person, not two kinds of person: what they are a CONTACT for (invoices,
 *  contracts, day-to-day) and whether they hold client-wide ACCESS. Somebody who is both used to
 *  appear twice on two tabs with neither row mentioning the other; they are one row now, joined on
 *  client_contact.app_user_id, which has meant exactly this since 0264 and was never used.
 *
 *  AND ARCHIVED IS A QUERY, NOT A COLOUR. The withdrawn row greyed out in the live list is gone:
 *  Active / Archived / All is the same control every other list in the product has, and it asks the
 *  server rather than dimming what came back. */
function PeoplePanel({ orgId, campaignId, campaignName, contacts, onChanged, pstatus, onStatus,
                       managers, managersErr, busy: outerBusy, onSetManager,
                       newManager, setNewManager, inviteFor, setInviteFor, inviteName, setInviteName }: {
  orgId: string; campaignId: string; campaignName: string;
  contacts: ClientContact[] | null; onChanged: () => Promise<void>;
  pstatus: "active" | "archived" | "all"; onStatus: (s: "active" | "archived" | "all") => void;
  managers: ClientManager[] | null; managersErr: string | null;
  busy: boolean; onSetManager: (login: string, active: boolean, invite?: boolean) => void;
  newManager: string; setNewManager: (v: string) => void;
  inviteFor: string | null; setInviteFor: (v: string | null) => void;
  inviteName: string; setInviteName: (v: string) => void;
}) {
  const blank = { id: null as string | null, name: "", email: "", phone: "",
                  for_invoices: false, for_contracts: false, for_daily: false,
                  pinned: false, note: "" };
  const [draft, setDraft] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function edit(k: ClientContact) {
    setErr(null);
    setDraft({ id: k.id, name: k.name, email: k.email || "", phone: k.phone || "",
               for_invoices: k.for_invoices, for_contracts: k.for_contracts, for_daily: k.for_daily,
               pinned: !!k.campaign_id, note: k.note || "" });
  }

  async function save() {
    setBusy(true); setErr(null);
    try {
      await api("/portal/client/contact", {
        method: "POST",
        body: JSON.stringify({
          org_id: orgId, id: draft.id, name: draft.name.trim(),
          email: draft.email.trim() || null, phone: draft.phone.trim() || null,
          for_invoices: draft.for_invoices, for_contracts: draft.for_contracts,
          for_daily: draft.for_daily,
          campaign_id: draft.pinned ? campaignId : null,
          note: draft.note.trim() || null,
        }),
      });
      setDraft(blank); await onChanged();
    } catch (e: any) { setErr(String(e.message || e)); }
    finally { setBusy(false); }
  }

  // ARCHIVE, NOT DELETE (0310). The wording follows: "remove" promised something the row no longer
  // does, and the row is what answers "who did we used to invoice".
  async function archive(k: ClientContact) {
    if (!(await confirmDialog(`Archive ${k.name}? They stop showing as current and keep their history.`,
                              { confirmLabel: "Archive" }))) return;
    setBusy(true); setErr(null);
    try { await api("/portal/client/contact/remove", { method: "POST", body: JSON.stringify({ org_id: orgId, id: k.id }) }); await onChanged(); }
    catch (e: any) { setErr(String(e.message || e)); }
    finally { setBusy(false); }
  }

  async function restore(k: ClientContact) {
    setBusy(true); setErr(null);
    try { await api("/portal/client/contact/restore", { method: "POST", body: JSON.stringify({ org_id: orgId, id: k.id }) }); await onChanged(); }
    catch (e: any) { setErr(String(e.message || e)); }
    finally { setBusy(false); }
  }

  const jobs = (k: ClientContact) =>
    [k.for_invoices && "Invoices", k.for_contracts && "Contracts", k.for_daily && "Day-to-day"]
      .filter(Boolean).join(" · ") || "No role set";

  // ── ONE ROW PER PERSON ────────────────────────────────────────────────────────────────────────
  // Joined on app_user_id where both halves know it. A contact with no login, and a login nobody
  // recorded as a contact, are each a row of their own -- both are real and common.
  type Row = { key: string; name: string; contact: ClientContact | null; access: ClientManager | null };
  const rows: Row[] = (() => {
    const byUser = new Map<string, Row>();
    const out: Row[] = [];
    for (const k of contacts || []) {
      const r: Row = { key: `c:${k.id}`, name: k.name, contact: k, access: null };
      if (k.app_user_id) byUser.set(k.app_user_id, r);
      out.push(r);
    }
    // The status filter applies to access too, so the toggle means one thing across the whole tab.
    for (const m of managers || []) {
      if (pstatus === "active" && !m.is_active) continue;
      if (pstatus === "archived" && m.is_active) continue;
      const hit = byUser.get(m.app_user_id);
      if (hit) { hit.access = m; continue; }
      out.push({ key: `m:${m.app_user_id}`, name: m.display_name || m.login_id, contact: null, access: m });
    }
    // Live first, then by name -- the same order the two lists had separately.
    return out.sort((a, b) => {
      const la = (a.contact && !a.contact.archived_at) || (a.access && a.access.is_active) ? 0 : 1;
      const lb = (b.contact && !b.contact.archived_at) || (b.access && b.access.is_active) ? 0 : 1;
      return la - lb || a.name.localeCompare(b.name);
    });
  })();

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {err && <div className="err">{err}</div>}

      <div className="row" style={{ gap: 4, alignItems: "center" }}>
        {(["active", "archived", "all"] as const).map((v) => (
          <button key={v} className={"btn sm" + (pstatus === v ? " accent" : "")}
                  onClick={() => onStatus(v)}>
            {v === "active" ? "Active" : v === "archived" ? "Archived" : "All"}
          </button>
        ))}
        <span className="muted" style={{ fontSize: 12, marginLeft: 6 }}>
          Everyone attached to this client — who you invoice and talk to, and who can open the
          campaigns. Most contacts never sign in.
        </span>
      </div>

      {/* A FAILED READ IS NOT AN EMPTY LIST, on either half. */}
      {managersErr && (
        <div className="err">
          Couldn&rsquo;t load who has access, so this is not an empty list. {managersErr}
        </div>
      )}

      {contacts === null || (managers === null && !managersErr)
        ? <div className="muted">Loading…</div>
        : rows.length === 0
          ? <div className="muted" style={{ fontSize: 13 }}>
              {pstatus === "archived"
                ? "Nobody archived on this client."
                : "Nobody on this client yet. Add a contact below, or grant somebody client-wide access."}
            </div>
          : (
            <table>
              <thead><tr>
                <th>Person</th><th>Contact for</th><th>Access</th><th></th>
              </tr></thead>
              <tbody>
                {rows.map((r) => {
                  const k = r.contact, m = r.access;
                  const gone = (!k || !!k.archived_at) && (!m || !m.is_active);
                  return (
                    <tr key={r.key} style={gone ? { opacity: 0.6 } : undefined}>
                      <td>
                        <div className="fld" style={{ marginBottom: 2 }}>{r.name}</div>
                        <div className="muted" style={{ fontSize: 12 }}>
                          {k?.email || m?.login_id || "—"}
                          {k?.phone ? ` · ${k.phone}` : ""}
                          {k?.campaign_name ? ` · pinned to ${k.campaign_name}` : ""}
                        </div>
                      </td>
                      <td className="muted" style={{ fontSize: 12.5 }}>
                        {k
                          ? <>
                              {jobs(k)}
                              {k.archived_at && <div style={{ fontSize: 11 }}>
                                archived {k.archived_at.slice(0, 10)}
                                {k.archived_by_name ? ` by ${k.archived_by_name}` : ""}
                              </div>}
                            </>
                          : "—"}
                      </td>
                      <td className="muted" style={{ fontSize: 12.5 }}>
                        {m
                          ? <>
                              {m.is_active ? "Client-wide manager" : "Withdrawn"}
                              <div style={{ fontSize: 11 }}>since {m.since ? m.since.slice(0, 10) : "—"}</div>
                            </>
                          : "—"}
                      </td>
                      <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                        {k && <>
                          <button className="btn sm" onClick={() => edit(k)}>Edit</button>{" "}
                          {k.archived_at
                            ? <button className="btn sm" disabled={busy} onClick={() => restore(k)}>Restore</button>
                            : <button className="btn sm warn" disabled={busy} onClick={() => archive(k)}>Archive</button>}
                          {" "}
                        </>}
                        {m && (m.is_active
                          ? <button className="btn sm warn" disabled={outerBusy}
                                    onClick={() => onSetManager(m.login_id, false)}>Withdraw access</button>
                          : <button className="btn sm" disabled={outerBusy}
                                    onClick={() => onSetManager(m.login_id, true)}>Restore access</button>)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

      {/* ── GRANTING ACCESS ────────────────────────────────────────────────────────────────────
          Its own control, below the list, for the same reason it had its own tab: this is the
          widest permission the product gives -- every campaign under the client, including ones
          that do not exist yet -- and it is granted by LOGIN, which a contact need not have. */}
      <div style={{ borderTop: "1px solid var(--border)", paddingTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
        <div className="fld">Give somebody client-wide access</div>
        <div className="muted" style={{ fontSize: 12 }}>
          Manager access for <b>every campaign under this client</b>, including new ones when they
          are created. It is how a consultant is attached to a whole account without being added
          race by race.
        </div>
        <div className="row" style={{ gap: 8 }}>
          <input className="ctl" style={{ maxWidth: 300 }} type="email" name="new-client-manager"
                 placeholder="their login address" aria-label="Login to grant client-wide access"
                 value={newManager}
                 onChange={(e) => { setNewManager(e.target.value); setInviteFor(null); }} />
          <button className="btn accent" disabled={!newManager.trim() || outerBusy}
                  onClick={() => onSetManager(newManager, true)}>Grant access</button>
        </div>
        {/* NO LOGIN YET: AN OFFER, NOT A DEAD END. A grant still refuses an address nobody holds,
            and that refusal stays -- a typo that quietly made an account would hand the widest
            access in the product to a login nobody meant to exist. So it becomes a question:
            the address is shown back, and inviting is a SECOND click. */}
        {inviteFor && (
          <div style={{ border: "1px solid var(--border,#2a3340)", borderRadius: 8, padding: 10 }}>
            <div className="fld" style={{ marginBottom: 4 }}>No Orator login for {inviteFor} yet</div>
            <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
              Invite them and they will be emailed a link to set their own password — nobody here
              types a credential for them. They get client-wide access as soon as the invite is
              sent. Check the address first; this creates an account.
            </div>
            <div className="row" style={{ gap: 8 }}>
              <input className="ctl" style={{ maxWidth: 260 }} value={inviteName}
                     aria-label="Their name" placeholder="their name (optional)"
                     onChange={(e) => setInviteName(e.target.value)} />
              <button className="btn accent" disabled={outerBusy}
                      onClick={() => onSetManager(inviteFor, true, true)}>Invite &amp; grant</button>
              <button className="btn sm" disabled={outerBusy} onClick={() => setInviteFor(null)}>Cancel</button>
            </div>
          </div>
        )}
        <div className="muted" style={{ fontSize: 12 }}>
          An address with no login is never granted access silently — you will be asked whether to
          invite them. Withdrawing keeps the row, so the record still shows they once had access.
        </div>
      </div>

      <div style={{ borderTop: "1px solid var(--border)", paddingTop: 12, display: "flex", flexDirection: "column", gap: 10 }}>
        <div className="fld">{draft.id ? "Edit contact" : "Add a contact"}</div>
        <div className="grid2">
          <label className="fld">Name<input className="ctl" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} /></label>
          <label className="fld">Email<input className="ctl" type="email" value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} /></label>
        </div>
        <div className="grid2">
          <label className="fld">Phone<input className="ctl" value={draft.phone} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} /></label>
          <label className="fld">Note<input className="ctl" value={draft.note} onChange={(e) => setDraft({ ...draft, note: e.target.value })} /></label>
        </div>
        <div className="row" style={{ gap: 14, flexWrap: "wrap" }}>
          <label className="row" style={{ gap: 6 }}><input type="checkbox" checked={draft.for_invoices} onChange={(e) => setDraft({ ...draft, for_invoices: e.target.checked })} /> Invoices</label>
          <label className="row" style={{ gap: 6 }}><input type="checkbox" checked={draft.for_contracts} onChange={(e) => setDraft({ ...draft, for_contracts: e.target.checked })} /> Contracts</label>
          <label className="row" style={{ gap: 6 }}><input type="checkbox" checked={draft.for_daily} onChange={(e) => setDraft({ ...draft, for_daily: e.target.checked })} /> Day-to-day</label>
          <button className="btn sm" type="button"
                  onClick={() => setDraft({ ...draft, for_invoices: true, for_contracts: true, for_daily: true })}>
            Same person for all three
          </button>
        </div>
        <label className="row" style={{ gap: 6 }}>
          <input type="checkbox" checked={draft.pinned} onChange={(e) => setDraft({ ...draft, pinned: e.target.checked })} />
          Only for “{campaignName}” — not the whole client
        </label>
        <div className="row" style={{ gap: 8 }}>
          <button className="btn accent" disabled={busy || !draft.name.trim()} onClick={save}>
            {busy ? "Saving…" : draft.id ? "Save contact" : "Add contact"}
          </button>
          {draft.id && <button className="btn" onClick={() => setDraft(blank)}>Cancel</button>}
        </div>
      </div>
    </div>
  );
}

function ManageModal({ account, onClose }: { account: Account; onClose: () => void }) {
  const [scopes, setScopes] = useState<Scope[]>([]);
  const [type, setType] = useState("state_house"); const [val, setVal] = useState("");
  const [fips, setFips] = useState<number | null>(null);
  // 0378: the city a city or council licence is for. Never a county -- that is `fips`.
  const [place, setPlace] = useState<number | null>(null);
  const [sizes, setSizes] = useState<ScopeSize[] | null>(null);
  const [totalSize, setTotalSize] = useState<{ households: number; voters: number } | null>(null);
  const [sizeErr, setSizeErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false); const [err, setErr] = useState<string | null>(null);
  const [pw, setPw] = useState(""); const [pwMsg, setPwMsg] = useState<string | null>(null);
  const [findings, setFindings] = useState<Finding[]>([]);
  const [checklist, setChecklist] = useState<ChecklistItem[]>([]);
  // These three panels had no loading state at all, so "No districts granted yet." / "No open
  // precinct anomalies." rendered before their fetches resolved — an account WITH districts briefly
  // claimed it had none. Flipped once per fetch, whether it succeeded or failed.
  // WHICH OF THE THREE JOBS THIS MODAL IS DOING. Not persisted: unlike the portal's own tab, this
  // one is opened FOR a reason — somebody clicked Licence or Access from the account row — and
  // remembering the last choice would drop the next person somewhere they did not ask for.
  const [mtab, setMTab] = useState<"licence" | "access" | "setup" | "credit">("licence");
  const [scopesRead, setScopesRead] = useState(false);
  const [dqRead, setDqRead] = useState(false);
  const [scopesErr, setScopesErr] = useState<string | null>(null);
  // VOLUNTEER PREFIXES (0261). Same three-answer discipline as the districts list above: loading,
  // could-not-ask and none-allocated are different facts, and "none" read off a failed request is
  // a materially wrong thing to tell staff about a client's namespace.
  type Pfx = { prefix: string; next_index: number; accounts: number };
  const [pfxs, setPfxs] = useState<Pfx[]>([]);
  const [pfxRead, setPfxRead] = useState(false);
  const [pfxErr, setPfxErr] = useState<string | null>(null);
  const [newPfx, setNewPfx] = useState("");
  const [findingsErr, setFindingsErr] = useState<string | null>(null);
  const [checklistErr, setChecklistErr] = useState<string | null>(null);

  async function resetAdminPw() {
    if (!account.holder_user_id || pw.trim().length < 6) { setPwMsg("Enter a password of at least 6 characters."); return; }
    setBusy(true); setPwMsg(null);
    try {
      await api("/members/set-password", { method: "POST", body: JSON.stringify({ campaign_id: account.campaign_id, app_user_id: account.holder_user_id, password: pw.trim() }) });
      setPwMsg("Password reset — give it to the admin to sign in."); setPw("");
    } catch (e: any) { setPwMsg(String(e.message || e)); }
    finally { setBusy(false); }
  }

  async function load() {
    try { setScopes((await api<{ scopes: Scope[] }>(`/portal/scopes?campaign_id=${account.campaign_id}`)).scopes); setScopesErr(null); }
    catch (e: any) { const m = String(e.message || e); setErr(m); setScopesErr(m); }
    finally { setScopesRead(true); }
    // Separate try: a failure to COUNT the turf must not make the turf itself look ungranted, and
    // a size that could not be read is not a size of zero.
    try {
      const r = await api<{ scopes: ScopeSize[]; total: { households: number; voters: number } }>(
        `/portal/scope-size?campaign_id=${account.campaign_id}`);
      setSizes(r.scopes); setTotalSize(r.total); setSizeErr(null);
    } catch (e: any) { setSizes(null); setTotalSize(null); setSizeErr(String(e?.message || e)); }
  }
  const sizeOf = (s: Scope) => (sizes || []).find(
    (z) => z.scope_type === s.scope_type && z.scope_value === s.scope_value
        && (z.scope_fips ?? null) === (s.scope_fips ?? null)
        && (z.scope_place ?? null) === (s.scope_place ?? null));
  async function loadDQ() {
    // SEPARATE TRIES. These shared one, so a findings failure suppressed the checklist as well — and
    // the single catch asserted a specific cause ("needs migration 0054") for ANY failure, including
    // a timeout on a database where 0054 has been applied for months.
    try {
      const f = await api<{ findings: Finding[] }>(`/portal/account/precinct-findings?state=${account.state}&campaign_id=${account.campaign_id}`);
      setFindings(f.findings); setFindingsErr(null);
    } catch (e: any) { setFindingsErr(String(e?.message || e)); }
    try {
      const c = await api<{ items: ChecklistItem[] }>(`/portal/account/checklist?campaign_id=${account.campaign_id}`);
      setChecklist(c.items); setChecklistErr(null);
    } catch (e: any) { setChecklistErr(String(e?.message || e)); }
    setDqRead(true);
  }
  async function toggleItem(it: ChecklistItem) {
    setChecklist((cs) => cs.map((x) => x.item_key === it.item_key ? { ...x, checked: !x.checked } : x));
    try { await api("/portal/account/checklist/set", { method: "POST", body: JSON.stringify({ campaign_id: account.campaign_id, item_key: it.item_key, checked: !it.checked, note: it.note }) }); }
    catch (e: any) { setErr(String(e.message || e)); loadDQ(); }
  }
  async function resolveFinding(f: Finding) {
    setBusy(true);
    try { await api("/portal/account/precinct-findings/resolve", { method: "POST", body: JSON.stringify({ finding_id: f.id, note: null }) }); await loadDQ(); }
    catch (e: any) { setErr(String(e.message || e)); }
    finally { setBusy(false); }
  }
  async function loadPfx() {
    try {
      setPfxs((await api<{ prefixes: Pfx[] }>(`/portal/account/prefixes?campaign_id=${account.campaign_id}`)).prefixes || []);
      setPfxErr(null);
    } catch (e: any) { setPfxErr(String(e?.message || e)); }
    finally { setPfxRead(true); }
  }
  async function grantPfx() {
    const p = newPfx.trim().toLowerCase();
    if (!p) return;
    setBusy(true); setPfxErr(null);
    try {
      setPfxs((await api<{ prefixes: Pfx[] }>("/portal/account/prefixes",
        { method: "POST", body: JSON.stringify({ campaign_id: account.campaign_id, add: [p] }) })).prefixes || []);
      setNewPfx("");
    } catch (e: any) { setPfxErr(String(e?.message || e)); }
    finally { setBusy(false); }
  }
  async function revokePfx(p: Pfx) {
    setBusy(true); setPfxErr(null);
    try {
      setPfxs((await api<{ prefixes: Pfx[] }>("/portal/account/prefixes",
        { method: "POST", body: JSON.stringify({ campaign_id: account.campaign_id, remove: [p.prefix] }) })).prefixes || []);
    } catch (e: any) { setPfxErr(String(e?.message || e)); }
    finally { setBusy(false); }
  }
  useEffect(() => { load(); loadDQ(); loadPfx(); }, []);

  async function grant() {
    if (!type || (type !== "state" && !val.trim())) return;
    // Said here as well as by the database, because the constraint violation reads as a bug in the
    // portal while this reads as the instruction it is.
    if (COUNTY_LOCAL.has(type) && fips == null) {
      setErr(`A ${SCOPE_LABEL[type] || type} licence needs a county — those codes repeat across counties.`);
      return;
    }
    if (PLACE_TYPES.has(type) && place == null) { setErr("Pick the city first."); return; }
    setBusy(true); setErr(null);
    try {
      await api("/portal/scope/grant", { method: "POST", body: JSON.stringify({
        campaign_id: account.campaign_id, state: account.state, scope_type: type,
        scope_value: val.trim() || null, scope_fips: fips,
        scope_place: PLACE_TYPES.has(type) ? place : null }) });
      setVal(""); setPlace(null); if (PLACE_TYPES.has(type)) setType("city"); await load();
    } catch (e: any) { setErr(String(e.message || e)); }
    finally { setBusy(false); }
  }
  async function revoke(s: Scope) {
    setBusy(true);
    // The county has to be named or this revokes the same code in every county the campaign holds.
    try { await api("/portal/scope/revoke", { method: "POST", body: JSON.stringify({ campaign_id: account.campaign_id, scope_type: s.scope_type, scope_value: s.scope_value, scope_fips: s.scope_fips, scope_place: s.scope_place ?? null }) }); await load(); }
    catch (e: any) { setErr(String(e.message || e)); }
    finally { setBusy(false); }
  }

  return (
    <div className="overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        {/* THE CAMPAIGN UNDER THE CLIENT (2026-10-26): Licence and Credit are this campaign's, not
            the whole client's, so the header says which one is open. */}
        <div className="head"><div>{account.org_name || account.campaign_name}
          {account.org_name && <div className="muted" style={{ fontSize: 12.5, fontWeight: 400 }}>{account.campaign_name}</div>}</div><button className="btn sm" style={{ marginLeft: "auto" }} onClick={onClose}>✕</button></div>
        {/* FIVE JOBS IN ONE SCROLL, now three tabs. Caleb: *"the Districts tab is a catch-all...
            we should probably rename this tab or divide it up."* It was headed "Districts" and held
            licensed turf, volunteer prefixes, the client admin login, precinct findings and the
            opening checklist — only the first of those is districts, and the header named the
            smallest thing in the modal.

            Nothing inside the sections changed. They were grouped, the header now names the CLIENT
            rather than one of its five contents, and each tab opens without a leading divider
            because the tab strip is the divider. */}
        <div className="row" style={{ gap: 4, padding: "8px 16px 0", borderBottom: "1px solid var(--border, #2a3340)" }}>
          {([["licence", "Licence"], ["access", "Access"], ["setup", "Setup"], ["credit", "Credit"]] as const).map(([k, label]) => (
            <button key={k} className="btn sm" onClick={() => setMTab(k)}
              aria-current={mtab === k ? "page" : undefined}
              style={mtab === k
                ? { borderBottom: "2px solid var(--accent, #1a8a4a)", borderRadius: "6px 6px 0 0", fontWeight: 600 }
                : { borderBottom: "2px solid transparent", borderRadius: "6px 6px 0 0", opacity: 0.7 }}>
              {label}
            </button>
          ))}
        </div>
        <div className="body" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {mtab === "licence" && <>
          <div className="muted" style={{ fontSize: 13 }}>What this client can see and cut. Granting a district immediately widens their licensed turf.</div>
          <div>
            {!scopesRead && <span className="muted">Loading…</span>}
            {/* This list IS the account's licensed turf. "None" read off a failed request is a
                materially wrong thing to tell someone about what a client has paid for. */}
            {scopesRead && scopes.length === 0 && (
              <span className="muted" style={scopesErr ? { color: "var(--warn,#eab308)" } : undefined}>
                {scopesErr ? "Couldn't load districts — this is not an empty grant." : "No districts granted yet."}
              </span>
            )}
            {scopes.map((s, i) => (
              <span className="chip" key={i}>
                {s.scope_type === "city" ? `City of ${s.place_name || s.scope_value || s.scope_place}`
                  : s.scope_type === "council" ? `${s.place_name || `City ${s.scope_place}`} District ${s.scope_value}`
                  : <>{SCOPE_LABEL[s.scope_type] || s.scope_type}{s.scope_value ? ` ${s.scope_value}` : ""}</>}
                {s.county ? <span className="muted"> · {s.county}</span> : null}
                {/* A city licence finds its doors through address records, which arrive with the
                    statewide rebuild -- a zero here is "not yet linked", not an empty city. */}
                {PLACE_TYPES.has(s.scope_type)
                  ? <span className="muted"> · doors after the address rebuild</span>
                  : sizeOf(s) ? <span className="muted" style={{ fontVariantNumeric: "tabular-nums" }}>
                  {" "}· {sizeOf(s)!.households.toLocaleString()} doors
                </span> : null}
                <button title="Revoke" disabled={busy} onClick={() => revoke(s)}>✕</button></span>
            ))}
          </div>
          <div className="row">
            <DistrictPicker state={account.state} scopeType={type} scopeValue={val} scopeFips={fips}
              scopePlace={place} onType={setType} onValue={setVal} onFips={setFips} onPlace={setPlace} />
            <button className="btn accent" disabled={busy} onClick={grant}>Grant</button>
          </div>
          {PLACE_TYPES.has(type) && <CityFacts state={account.state} place={place} scopeType={type} district={val} />}
          {/* The number next to the thing being priced. Deliberately NOT the sum of the chips above:
              scopes union, so a precinct inside a district is one door, not two. */}
          {sizeErr && <div className="err">
            Couldn&rsquo;t count the licensed turf, so this is not a count of zero. {sizeErr}
          </div>}
          {totalSize && (
            <div className="muted" style={{ fontSize: 13, fontVariantNumeric: "tabular-nums" }}>
              Licensed turf: <b>{totalSize.households.toLocaleString()}</b> doors,{" "}
              <b>{totalSize.voters.toLocaleString()}</b> voters
              {scopes.length > 1 ? " — counted once each, so it is less than the chips add up to" : ""}
            </div>
          )}
          </>}
          {mtab === "access" && <>
          <div>
            <div className="fld" style={{ marginBottom: 6 }}>Volunteer prefixes</div>
            <div className="muted" style={{ fontSize: 13, marginBottom: 8 }}>
              What this client's quick volunteers sign in under. Claimed across the whole platform —
              once accounts exist under a prefix it can't be withdrawn or given to anyone else, so
              the count beside each one is what matters.
            </div>
            <div>
              {!pfxRead && <span className="muted">Loading…</span>}
              {pfxRead && pfxs.length === 0 && (
                <span className="muted" style={pfxErr ? { color: "var(--warn,#eab308)" } : undefined}>
                  {pfxErr ? "Couldn't load prefixes — this is not an empty allocation."
                          : "None assigned — this client cannot create quick volunteers yet."}
                </span>
              )}
              {pfxs.map((p) => (
                <span className="chip" key={p.prefix}>
                  {p.prefix}{p.accounts > 0 ? ` · ${p.accounts}` : ""}
                  {/* No ✕ once logins exist under it. Withdrawing the row would not delete
                      `<prefix>1@volunteers.local`; it would leave it signing in under a prefix the
                      campaign no longer holds, and free the same prefix to collide with it. */}
                  {p.accounts === 0 && <button title="Withdraw" disabled={busy} onClick={() => revokePfx(p)}>✕</button>}
                </span>
              ))}
            </div>
            <div className="row" style={{ marginTop: 8 }}>
              <input className="ctl" value={newPfx} placeholder="clientshorthand"
                autoCapitalize="none" autoCorrect="off" spellCheck={false}
                onChange={(e) => setNewPfx(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !busy) grantPfx(); }} />
              <button className="btn accent" disabled={busy || !newPfx.trim()} onClick={grantPfx}>Assign</button>
            </div>
            {pfxErr && pfxs.length > 0 && <div className="err" style={{ marginTop: 6 }}>{pfxErr}</div>}
          </div>
          {account.holder_user_id && (
            <div style={{ borderTop: "1px solid var(--border, #2a3340)", paddingTop: 12 }}>
              <div className="fld" style={{ marginBottom: 6 }}>Client admin login</div>
              <div className="muted" style={{ fontSize: 13, marginBottom: 8 }}>Set or reset the admin's password directly (no email). Use this if they're locked out.</div>
              <div className="row">
                <input className="ctl" type="text" placeholder="New password (6+ chars)" value={pw} onChange={(e) => setPw(e.target.value)} />
                <button className="btn" disabled={busy || pw.trim().length < 6} onClick={resetAdminPw}>Reset password</button>
              </div>
              {pwMsg && <div className="muted" style={{ fontSize: 13, marginTop: 6 }}>{pwMsg}</div>}
            </div>
          )}
          </>}
          {mtab === "credit" && <CreditTab campaignId={account.campaign_id} />}
          {mtab === "setup" && <>
          <div>
            <div className="fld" style={{ marginBottom: 6 }}>Account opening checklist</div>
            {!dqRead && <div className="muted" style={{ fontSize: 13 }}>Loading…</div>}
            {dqRead && checklist.length === 0 && (
              <div className="muted" style={{ fontSize: 13, ...(checklistErr ? { color: "var(--warn,#eab308)" } : {}) }}>
                {checklistErr ? `Couldn't load the checklist — ${checklistErr}` : "Checklist appears once migration 0054 is applied."}
              </div>
            )}
            {/* long-list-ok -- the account checklist is the fixed set 0054 seeds per campaign, not
                a list that grows with the data. */}
            {checklist.map((it) => (
              <label key={it.item_key} className="row" style={{ alignItems: "center", gap: 8, marginBottom: 6, cursor: "pointer" }}>
                <input type="checkbox" checked={it.checked} onChange={() => toggleItem(it)} />
                <span style={{ fontSize: 13, textDecoration: it.checked ? "line-through" : "none", opacity: it.checked ? 0.6 : 1 }}>{it.label}</span>
              </label>
            ))}
          </div>
          <div style={{ borderTop: "1px solid var(--border, #2a3340)", paddingTop: 12 }}>
            <div className="fld" style={{ marginBottom: 6 }}>Precinct data quality</div>
            {findings.length === 0 ? (
              <div className="muted" style={{ fontSize: 13, ...(findingsErr ? { color: "var(--warn,#eab308)" } : {}) }}>
                {!dqRead ? "Loading…" : findingsErr ? "Couldn't check for anomalies — this is not a clean result." : "No open precinct anomalies."}
              </div>
            ) : (
              <>
                <div className="muted" style={{ fontSize: 13, marginBottom: 8 }}>Precincts in the voter data needing attention. Non-blocking — resolve as boundaries are loaded / the file is verified.</div>
                {findings.map((f) => (
                  <div className="row" key={f.id} style={{ alignItems: "center", gap: 8, marginBottom: 6 }}>
                    <span className="chip">{f.finding_type === "missing_polygon" ? "No map polygon" : "File ≠ map"}</span>
                    <span style={{ fontSize: 13 }}>Precinct <b>{f.precinct || "—"}</b> · ~{f.voter_count} {f.finding_type === "missing_polygon" ? "voters" : "households"}</span>
                    <button className="btn sm" style={{ marginLeft: "auto" }} disabled={busy} onClick={() => resolveFinding(f)}>Mark resolved</button>
                  </div>
                ))}
              </>
            )}
          </div>
          </>}
          {err && <div className="err">{err}</div>}
        </div>
        <div className="foot"><button className="btn" style={{ marginLeft: "auto" }} onClick={onClose}>Done</button></div>
      </div>
    </div>
  );
}

type AuditEvent = {
  id: string; action: string; detail: string | null; at: string | null;
  actor: string | null; actor_user_id: string | null;
  target: string | null; target_user_id: string | null;
};

const ACTION_LABEL: Record<string, string> = {
  open_account: "Opened account", close_account: "Closed account", reopen_account: "Reopened account",
  attach_admin: "Attached admin", grant_scope: "Granted district", revoke_scope: "Revoked district",
  set_platform_role: "Set platform role", set_platform_active: "Set platform access",
  set_account_holder: "Set account holder",
  // swap_lead_manager is RETIRED (0275) but stays in this map: the audit log is history, and rows
  // written before the retirement must still render as words rather than as a raw action name.
  swap_lead_manager: "Swapped lead manager", set_holder_identity: "Renamed account holder",
  break_glass_restore_owner: "Break-glass owner restore",
};
const fmtAction = (a: string) => ACTION_LABEL[a] || a.replace(/_/g, " ");
const fmtWhen = (s: string | null) => (s ? new Date(s).toLocaleString() : "\u2014");

function AuditLog() {
  const PAGE = 50;
  const [actions, setActions] = useState<string[]>([]);
  const [action, setAction] = useState(""); const [q, setQ] = useState("");
  const [since, setSince] = useState(""); const [until, setUntil] = useState("");
  const [offset, setOffset] = useState(0);
  // Two separate effects used to call load() — one on filter change, one debounced on the search box —
  // each resetting the offset, so a filter change fired two requests and the first could paint "No
  // activity matches these filters" before the real rows landed. One query, keyed on everything, with
  // stale responses discarded.
  const filterKey = [action, q.trim(), since, until].join("|");
  const effOffset = usePagedKey(filterKey, offset, setOffset);
  const { query, reload } = useListQuery<{ events: AuditEvent[]; total: number; actions: string[] }>(
    `${filterKey}|${effOffset}`,
    async () => {
      const p = new URLSearchParams();
      if (action) p.set("action", action);
      if (q.trim()) p.set("q", q.trim());
      if (since) p.set("since", since);
      if (until) p.set("until", until);
      p.set("limit", String(PAGE)); p.set("offset", String(effOffset));
      return api<{ events: AuditEvent[]; total: number; actions: string[] }>(`/portal/audit?${p}`);
    },
    { debounce: 300 },
  );
  const load = (_off?: number) => reload();
  const data = queryData(query);
  const events = data?.events ?? [];
  const total = data?.total ?? 0;
  useEffect(() => { if (data?.actions?.length) setActions(data.actions); }, [data]);

  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + PAGE, total);
  const hasFilter = !!(action || q || since || until);

  return (
    <div className="wrap">
      <div className="row" style={{ marginBottom: 14 }}>
        <h2 style={{ margin: 0 }}>Activity log</h2>
        <span className="muted">{total} event{total === 1 ? "" : "s"}</span>
        <button className="btn sm" style={{ marginLeft: "auto" }} onClick={() => load()}>Refresh</button>
      </div>
      <div className="card" style={{ padding: 12, marginBottom: 12 }}>
        <div className="row" style={{ gap: 10, flexWrap: "wrap" }}>
          <select className="ctl" style={{ maxWidth: 210 }} value={action} onChange={(e) => setAction(e.target.value)}>
            <option value="">All actions</option>
            {actions.map((a) => <option key={a} value={a}>{fmtAction(a)}</option>)}
          </select>
          <input className="ctl" style={{ maxWidth: 240 }} placeholder="Search detail / person" aria-label="Search by detail or person" name="detail-search" value={q} onChange={(e) => setQ(e.target.value)} />
          <label className="muted" style={{ fontSize: 12 }}>From <input className="ctl" type="date" value={since} onChange={(e) => setSince(e.target.value)} onClick={(e) => e.currentTarget.showPicker?.()} style={{ cursor: "pointer" }} /></label>
          <label className="muted" style={{ fontSize: 12 }}>To <input className="ctl" type="date" value={until} onChange={(e) => setUntil(e.target.value)} onClick={(e) => e.currentTarget.showPicker?.()} style={{ cursor: "pointer" }} /></label>
          {hasFilter && <button className="btn sm" onClick={() => { setAction(""); setQ(""); setSince(""); setUntil(""); }}>Clear</button>}
        </div>
      </div>
      {query.status === "error" && events.length > 0 &&
        <div className="err" style={{ marginBottom: 10 }}>Couldn’t refresh — showing the last results.</div>}
      <div className="card">
        <table>
          <thead><tr><th>When</th><th>Actor</th><th>Action</th><th>Target</th><th>Detail</th></tr></thead>
          <tbody>
            {query.status === "loading" && <tr><td colSpan={5} className="muted">Loading…</td></tr>}
            {query.status === "error" && events.length === 0 &&
              <tr><td colSpan={5} className="err">Couldn’t load the activity log — this is a failure, not an empty log. <button className="btn sm" onClick={() => reload()}>Try again</button></td></tr>}
            {query.status !== "loading" && !(query.status === "error" && events.length === 0) && events.length === 0 &&
              <tr><td colSpan={5} className="muted">No activity matches these filters.</td></tr>}
            {events.map((e) => (
              <tr key={e.id}>
                <td className="muted" style={{ whiteSpace: "nowrap" }}>{fmtWhen(e.at)}</td>
                <td>{e.actor || "\u2014"}</td>
                <td><span className="tag">{fmtAction(e.action)}</span></td>
                <td>{e.target || "\u2014"}</td>
                <td className="muted" style={{ wordBreak: "break-word" }}>{e.detail || "\u2014"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {total > PAGE && (
        <div className="row" style={{ marginTop: 12, justifyContent: "flex-end" }}>
          <span className="muted" style={{ fontSize: 13 }}>{from}–{to} of {total}</span>
          <button className="btn sm" disabled={offset === 0 || isFetching(query)} onClick={() => setOffset(Math.max(0, offset - PAGE))}>Prev</button>
          <button className="btn sm" disabled={to >= total || isFetching(query)} onClick={() => setOffset(offset + PAGE)}>Next</button>
        </div>
      )}
    </div>
  );
}

type StaffUser = {
  id: string; login_id: string; display_name: string | null;
  platform_role: string; is_active: boolean; created_at: string | null;
};

// Team tab (0098) — Orator platform staff. Any staff can view; only an OWNER can invite,
// change roles, or enable/disable. Role changes ride the owner-only orator_set_platform_role;
// new logins get an emailed Supabase set-password invite (no typed passwords).
function StaffTab({ owner }: { owner: boolean }) {
  const [staff, setStaff] = useState<StaffUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [findings, setFindings] = useState<Record<string, number>>({});
  const [invite, setInvite] = useState(false);

  const firstStaff = useFirstLoad();   // lib/quiet.ts: inviting or changing someone re-reads quietly
  async function load() {
    if (firstStaff.isNew("staff")) setLoading(true);
    setErr(null);
    try { setStaff((await api<{ staff: StaffUser[] }>("/portal/staff")).staff); firstStaff.done("staff"); }
    catch (e: any) { setErr(String(e.message || e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function setRole(u: StaffUser, role: string | null) {
    if (role === u.platform_role) return;
    if (role === null && !(await confirmDialog(`Remove platform access for ${u.display_name || u.login_id}?`, { danger: true, confirmLabel: "Remove access" }))) return;
    if (role === "owner" && !(await confirmDialog(`Make ${u.display_name || u.login_id} a platform OWNER? Owners can manage all staff and every client's data.`, { confirmLabel: "Make owner" }))) return;
    setBusyId(u.id); setErr(null); setMsg(null);
    try { await api("/portal/staff/set-role", { method: "POST", body: JSON.stringify({ app_user_id: u.id, role }) }); await load(); setMsg("Role updated."); }
    catch (e: any) { setErr(String(e.message || e)); }
    finally { setBusyId(null); }
  }
  async function setActive(u: StaffUser, active: boolean) {
    if (!active && !(await confirmDialog(`Disable ${u.display_name || u.login_id}? They lose portal access immediately (reversible).`, { danger: true, confirmLabel: "Disable" }))) return;
    setBusyId(u.id); setErr(null); setMsg(null);
    try { await api("/portal/staff/active", { method: "POST", body: JSON.stringify({ app_user_id: u.id, active }) }); await load(); }
    catch (e: any) { setErr(String(e.message || e)); }
    finally { setBusyId(null); }
  }

  return (
    <div className="wrap">
      <div className="row" style={{ marginBottom: 14 }}>
        <h2 style={{ margin: 0 }}>Team</h2>
        <span className="muted">{staff.length} staff</span>
        {owner && <button className="btn accent" style={{ marginLeft: "auto" }} onClick={() => setInvite(true)}>＋ Add staff</button>}
      </div>
      {!owner && <div className="muted" style={{ marginBottom: 10, fontSize: 13 }}>You can view the team. Only a platform owner can invite or change staff.</div>}
      {err && <div className="err" style={{ marginBottom: 10 }}>{err}</div>}
      {msg && <div className="muted" style={{ marginBottom: 10, fontSize: 13 }}>{msg}</div>}
      <div className="card">
        <table>
          <thead><tr><th>Name</th><th>Email / login</th><th>Role</th><th>Status</th><th>Added</th>{owner && <th></th>}</tr></thead>
          <tbody>
            {loading && <tr><td colSpan={owner ? 6 : 5} className="muted">Loading…</td></tr>}
            {!loading && staff.length === 0 && (
              <tr><td colSpan={owner ? 6 : 5} className="muted" style={err ? { color: "var(--warn,#eab308)" } : undefined}>
                {err ? "Couldn't load the team — this is not an empty list." : "No staff yet."}
              </td></tr>
            )}
            {!loading && staff.map((u) => (
              <tr key={u.id} style={{ opacity: u.is_active ? 1 : 0.55 }}>
                <td style={{ fontWeight: 700 }}>{u.display_name || "—"}</td>
                <td className="muted" style={{ userSelect: "text" }}>{u.login_id}</td>
                <td><span className={"badge " + (u.platform_role === "owner" ? "active" : "")}>{u.platform_role}</span></td>
                <td><span className={"badge " + (u.is_active ? "active" : "closed")}>{u.is_active ? "active" : "disabled"}</span></td>
                <td className="muted" style={{ whiteSpace: "nowrap" }}>{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                {owner && (
                  <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    <select className="ctl" style={{ maxWidth: 150, display: "inline-block" }} value={u.platform_role}
                      disabled={busyId === u.id} onChange={(e) => setRole(u, e.target.value === "none" ? null : e.target.value)}>
                      <option value="staff">Staff</option>
                      <option value="owner">Owner</option>
                      <option value="none">Remove access</option>
                    </select>{" "}
                    {u.is_active
                      ? <button className="btn sm warn" disabled={busyId === u.id} onClick={() => setActive(u, false)}>Disable</button>
                      : <button className="btn sm" disabled={busyId === u.id} onClick={() => setActive(u, true)}>Enable</button>}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {invite && <InviteStaffModal onClose={() => setInvite(false)} onDone={(note) => { setInvite(false); if (note) setMsg(note); load(); }} />}
    </div>
  );
}

function InviteStaffModal({ onClose, onDone }: { onClose: () => void; onDone: (note?: string) => void }) {
  const [email, setEmail] = useState(""); const [name, setName] = useState(""); const [role, setRole] = useState("staff");
  const [busy, setBusy] = useState(false); const [err, setErr] = useState<string | null>(null);
  const valid = /\S+@\S+\.\S+/.test(email.trim());
  async function submit() {
    setBusy(true); setErr(null);
    try {
      const r = await api<{ invited: boolean }>("/portal/staff/invite", {
        method: "POST", body: JSON.stringify({ email: email.trim(), name: name.trim() || null, role }),
      });
      onDone(r.invited ? "Invite emailed — they'll set their own password from the link." : "Existing login added as staff.");
    } catch (e: any) { setErr(String(e.message || e)); setBusy(false); }
  }
  return (
    <div className="overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 460 }}>
        <div className="head">Add a staff member<button className="btn sm" style={{ marginLeft: "auto" }} onClick={onClose}>✕</button></div>
        <div className="body" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <label className="fld">Email<input className="ctl" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="pat@orator.com" /></label>
          <label className="fld">Name<input className="ctl" value={name} onChange={(e) => setName(e.target.value)} placeholder="Pat Rivera" /></label>
          <label className="fld">Role
            <select className="ctl" value={role} onChange={(e) => setRole(e.target.value)}>
              <option value="staff">Staff — cross-account oversight</option>
              <option value="owner">Owner — full platform control</option>
            </select>
          </label>
          <div className="muted" style={{ fontSize: 12, marginTop: -4 }}>A new email gets a Supabase set-password invite link and picks their own password. An email that already has an Orator login is just granted the staff role.</div>
          {err && <div className="err">{err}</div>}
        </div>
        <div className="foot">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn accent" style={{ marginLeft: "auto" }} disabled={busy || !valid} onClick={submit}>{busy ? "Adding…" : "Send invite"}</button>
        </div>
      </div>
    </div>
  );
}

/** One destination in the left rail.
 *
 *  A <button> and not a NavLink: the portal has no router -- it switches on a `tab` string -- so
 *  there is nothing for a NavLink to link to. The classes are the dashboard AppShell's, deliberately
 *  copied rather than approximated, so the two products do not drift into looking almost the same.
 */
function RailItem({ on, onClick, children }:
                  { on: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} aria-current={on ? "page" : undefined}
      className={`w-full text-left flex items-center gap-3 mx-2 my-0.5 px-3 py-2 rounded-lg font-semibold text-sm border-0 bg-transparent cursor-pointer ${
        on ? "bg-accent-weak text-accent" : "text-muted hover:bg-surface-2 hover:text-text"
      }`} style={{ width: "calc(100% - 1rem)" }}>
      {children}
    </button>
  );
}

function RailSection({ label }: { label: string }) {
  return <div className="px-4 pt-3 pb-1 text-2xs font-bold uppercase tracking-wider text-muted">{label}</div>;
}

// 0265 — combining two clients that are one client. 0263 made the duplicate visible and refused to
// repair it: org membership is cross-campaign, so merging hands everyone on one side sight of the
// other, and that is a person's call. This is how a person makes it — the preview is computed
// against the same rows the merge will move, so what you are agreeing to is on the screen.
type MergeChange = { kind: string; who: string; detail: string };
type NamedClient = { org_id: string; name: string; campaigns: number; created_at: string | null };

/** PUT A CAMPAIGN UNDER A CLIENT — one that exists, or a new one by name (0270).
 *
 *  WHY THIS IS NOT A FIELD ON THE EDIT FORM. It already was, and it did nothing: the Edit form
 *  sends a client name to orator_update_account, which applies it inside `if org is not null`, so
 *  for a campaign with no client the save reported success and changed nothing. Rather than fix the
 *  silent field, the operation gets its own screen, because it is not an edit — it MOVES the
 *  campaign, and client membership is cross-campaign, so every client-level manager of the
 *  destination gains a campaign they could not see a moment ago. That is worth a sentence in front
 *  of somebody rather than a text box they fill in by accident. */
function SetClientModal({ account, clients, onClose, onDone }: {
  account: Account; clients: Client[]; onClose: () => void; onDone: () => void;
}) {
  const [orgId, setOrgId] = useState<string>(account.org_id || "");
  const [newName, setNewName] = useState("");
  const [mode, setMode] = useState<"existing" | "new">(clients.length ? "existing" : "new");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const target = clients.find((c) => c.org_id === orgId);
  const ready = mode === "existing" ? !!orgId && orgId !== account.org_id : newName.trim().length > 0;

  async function save() {
    if (!ready) return;
    setBusy(true); setErr(null);
    try {
      await api("/portal/campaign/client", { method: "POST", body: JSON.stringify(
        mode === "existing" ? { campaign_id: account.campaign_id, org_id: orgId }
                            : { campaign_id: account.campaign_id, new_name: newName.trim() })});
      onDone();
    } catch (e: any) { setErr(String(e.message || e)); setBusy(false); }
  }

  return (
    <div className="overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
        <div className="head">Client for “{account.campaign_name}”
          <button className="btn sm" style={{ marginLeft: "auto" }} onClick={onClose}>✕</button></div>
        <div className="body" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {err && <div className="err">{err}</div>}
          <div className="muted" style={{ fontSize: 13 }}>
            {account.org_name
              ? <>Currently under <strong>{account.org_name}</strong>.</>
              : <>This campaign is under no client, so nobody has client-wide access to it.</>}
          </div>

          <div className="row" style={{ gap: 14 }}>
            <label className="row" style={{ gap: 6 }}>
              <input type="radio" name="client-mode" checked={mode === "existing"}
                     disabled={!clients.length} onChange={() => setMode("existing")} />
              <span>A client I already have</span>
            </label>
            <label className="row" style={{ gap: 6 }}>
              <input type="radio" name="client-mode" checked={mode === "new"}
                     onChange={() => setMode("new")} />
              <span>A new client</span>
            </label>
          </div>

          {mode === "existing" ? (
            <select className="ctl" value={orgId} onChange={(e) => setOrgId(e.target.value)} aria-label="Client">
              <option value="">Choose a client…</option>
              {clients.map((c) => (
                <option key={c.org_id} value={c.org_id}>
                  {c.name} ({c.campaigns} {c.campaigns === 1 ? "campaign" : "campaigns"})
                </option>
              ))}
            </select>
          ) : (
            <input className="ctl" placeholder="Client name" value={newName} name="new-client-name"
                   aria-label="New client name" onChange={(e) => setNewName(e.target.value)} />
          )}

          {/* THE CONSEQUENCE, AT THE MOMENT OF THE CLICK. Moving a campaign moves who can see it,
              and the number is already on screen — there is no reason to make somebody guess. */}
          {mode === "existing" && target && target.members > 0 && (
            <div className="muted" style={{ fontSize: 13 }}>
              {target.members} {target.members === 1 ? "person" : "people"} on “{target.name}” —
              anyone with client-wide access there will be able to see this campaign.
            </div>
          )}
          {mode === "new" && newName.trim() && clients.some((c) => c.name.trim().toLowerCase() === newName.trim().toLowerCase()) && (
            <div className="muted" style={{ fontSize: 13, color: "var(--warn,#eab308)" }}>
              You already have a client called “{newName.trim()}”. Two clients may genuinely share a
              name, so this is allowed — but if they are the same client, pick it above instead.
              A second record is how a campaign ends up invisible to its own manager.
            </div>
          )}
        </div>
        <div className="foot">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn accent" disabled={!ready || busy} onClick={save}>
            {busy ? "Saving…" : mode === "new" ? "Create client and move" : "Move to this client"}
          </button>
        </div>
      </div>
    </div>
  );
}

function MergeModal({ name, accounts, onClose, onDone }: {
  name: string; accounts: Account[]; onClose: () => void; onDone: () => void;
}) {
  const [clients, setClients] = useState<NamedClient[] | null>(null);
  const [keep, setKeep] = useState<string | null>(null);
  const [absorb, setAbsorb] = useState<string | null>(null);
  const [changes, setChanges] = useState<MergeChange[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api<{ clients: NamedClient[] }>(`/portal/clients/named?name=${encodeURIComponent(name)}`);
        setClients(r.clients);
        // The default is the record that already carries the most, then the older one — the
        // shape a person would pick if they had to. It is only a default: either can survive,
        // and the radio names the campaigns under each so the choice is made on sight and not
        // on two identical labels with different dates.
        const ranked = [...r.clients].sort((a, b) =>
          b.campaigns - a.campaigns || (a.created_at || "").localeCompare(b.created_at || ""));
        if (ranked.length >= 2) {
          setKeep(ranked[0].org_id);
          if (ranked.length === 2) setAbsorb(ranked[1].org_id);
        }
      } catch (e: any) { setErr(String(e.message || e)); }
    })();
  }, [name]);

  useEffect(() => {
    if (!keep || !absorb || keep === absorb) { setChanges(null); return; }
    let live = true;
    (async () => {
      setErr(null);
      try {
        const r = await api<{ changes: MergeChange[] }>(
          `/portal/clients/merge-preview?keep=${encodeURIComponent(keep)}&absorb=${encodeURIComponent(absorb)}`);
        if (live) setChanges(r.changes);
      } catch (e: any) {
        // Never an empty preview on a failed read: "nothing changes" is the one answer that would
        // make somebody press the button.
        if (live) { setChanges(null); setErr(String(e.message || e)); }
      }
    })();
    return () => { live = false; };
  }, [keep, absorb]);

  function pickKeep(id: string) {
    setKeep(id);
    if (absorb === id) setAbsorb(null);
    if (clients && clients.length === 2) setAbsorb(clients.find((c) => c.org_id !== id)!.org_id);
  }

  // No second "are you sure?". This whole screen IS the confirmation, and it can show the
  // consequence a generic dialog cannot — so the consequence goes on the button instead, where
  // it is read at the moment of the click rather than one overlay later.
  async function doMerge() {
    if (!keep || !absorb) return;
    setBusy(true); setErr(null);
    try {
      await api("/portal/clients/merge", { method: "POST", body: JSON.stringify({ keep, absorb }) });
      onDone();
    } catch (e: any) { setErr(String(e.message || e)); setBusy(false); }
  }

  const by = (k: string) => (changes || []).filter((c) => c.kind === k);
  const ready = !!keep && !!absorb && keep !== absorb && changes !== null;
  const gains = by("access").length;
  // Campaign names come from the accounts table the page already loaded, so telling two clients
  // apart costs no extra request.
  const campaignsUnder = (orgId: string) =>
    accounts.filter((a) => a.org_id === orgId).map((a) => a.campaign_name);

  return (
    <div className="overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 680 }} onClick={(e) => e.stopPropagation()}>
        <div className="head">Combine “{name}”<button className="btn sm" style={{ marginLeft: "auto" }} onClick={onClose}>✕</button></div>
        <div className="body" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {err && <div className="err">{err}</div>}
          {clients === null && !err && <div className="muted">Loading the clients…</div>}

          {clients && (<>
            <div className="fld">Which one survives?</div>
            <div className="muted" style={{ fontSize: 12, marginTop: -6 }}>
              Either can. The survivor keeps its own name and details, and takes on anything the
              other had that it was missing.
            </div>
            {clients.map((c) => (
              <label key={c.org_id} className="row" style={{ gap: 8, alignItems: "baseline" }}>
                <input type="radio" name="merge-keep" checked={keep === c.org_id} onChange={() => pickKeep(c.org_id)} />
                <span style={{ minWidth: 0 }}>
                  {/* Two clients with the same name are told apart by what is UNDER them, not by
                      a count and a date. That is the whole reason this screen is confusing. */}
                  <strong>{campaignsUnder(c.org_id).join(", ") || "No campaigns"}</strong>
                  <span className="muted" style={{ fontSize: 12.5 }}>
                    {c.created_at ? ` — opened ${new Date(c.created_at).toLocaleDateString()}` : ""}
                  </span>
                </span>
              </label>
            ))}
            {clients.length > 2 && (
              <>
                <div className="fld" style={{ marginTop: 4 }}>Which one folds into it first?</div>
                {clients.filter((c) => c.org_id !== keep).map((c) => (
                  <label key={c.org_id} className="row" style={{ gap: 8, alignItems: "baseline" }}>
                    <input type="radio" name="merge-absorb" checked={absorb === c.org_id} onChange={() => setAbsorb(c.org_id)} />
                    <span style={{ minWidth: 0 }}>
                      <strong>{campaignsUnder(c.org_id).join(", ") || "No campaigns"}</strong>
                      <span className="muted" style={{ fontSize: 12.5 }}>
                        {c.created_at ? ` — opened ${new Date(c.created_at).toLocaleDateString()}` : ""}
                      </span>
                    </span>
                  </label>
                ))}
                <div className="muted" style={{ fontSize: 12 }}>
                  {clients.length} clients share this name. They combine two at a time, so you will
                  come back here {clients.length - 1 === 1 ? "once" : `${clients.length - 1} times`} in
                  all — each pass shows its own preview.
                </div>
              </>
            )}
          </>)}

          {ready && (
            <div style={{ borderTop: "1px solid var(--border)", paddingTop: 12, display: "flex", flexDirection: "column", gap: 10 }}>
              <div className="fld">What this changes</div>

              <MergeGroup title="Campaigns that move" rows={by("campaign")} empty="None." />
              <MergeGroup
                title="Who gains access"
                rows={by("access")}
                empty="Nobody. Everyone who would end up on the combined client can already see all of these campaigns."
                warn={by("access").length > 0}
              />
              <MergeGroup title="People who come with" rows={by("contact")} empty="None." />
              <MergeGroup title="Memberships that become one" rows={by("fold")} empty="None." />
              <MergeGroup
                title="Details that differ — the survivor's are kept"
                rows={by("kept")}
                empty="The two agree on everything they both filled in."
                warn={by("kept").length > 0}
              />

              <div className="muted" style={{ fontSize: 12 }}>
                The other client record is deleted once its campaigns, people and memberships have
                moved. Nothing else is removed, and the merge is written to the activity log.
              </div>
            </div>
          )}
        </div>
        <div className="foot">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className={"btn " + (gains > 0 ? "warn" : "accent")} style={{ marginLeft: "auto" }}
                  disabled={busy || !ready} onClick={doMerge}>
            {busy ? "Combining…"
                  : gains > 0 ? `Combine — ${gains} ${gains === 1 ? "person gains" : "people gain"} access`
                  : "Combine into one client"}
          </button>
        </div>
      </div>
    </div>
  );
}

function MergeGroup({ title, rows, empty, warn }: {
  title: string; rows: MergeChange[]; empty: string; warn?: boolean;
}) {
  return (
    <div>
      <div className="muted" style={{ fontSize: 11.5, letterSpacing: ".05em", textTransform: "uppercase", marginBottom: 3 }}>
        {title}
      </div>
      {rows.length === 0
        ? <div className="muted" style={{ fontSize: 13 }}>{empty}</div>
        : rows.map((r, i) => (
            <div key={i} style={{ fontSize: 13.5, color: warn ? "var(--warn,#eab308)" : undefined }}>
              <strong>{r.who}</strong> <span className="muted">{r.detail}</span>
            </div>
          ))}
    </div>
  );
}

// 0266 — what is breaking. Recorded always, acted on selectively.
//
// The grouped view is the default and the point: 616 AbortErrors sat unread in field_event for
// months, and a screen that shows them as 616 rows is a screen nobody finishes. One line per
// (source, kind), newest and most-open first, expand for the occurrences.
type OpsGroup = {
  source: string; kind: string; severity: string; occurrences: number;
  first_at: string | null; last_at: string | null; latest_summary: string; open_count: number;
};
type OpsEvent = {
  id: string; at: string | null; source: string; severity: string; kind: string; summary: string;
  detail: any; campaign_id: string | null; campaign_name: string | null; login_id: string | null;
  resolved_at: string | null;
};
const OPS_SOURCE_LABEL: Record<string, string> = {
  api: "Our API", field: "Field app", github: "CI / deploys", resend: "Email",
  capgo: "App updates", render: "Hosting", vault: "Address vault", portal: "Portal",
};
const SEV_COLOR: Record<string, string> = {
  error: "var(--danger,#e05252)", warn: "var(--warn,#eab308)", info: "var(--muted,#8b949e)",
};

function Health() {
  const [days, setDays] = useState(7);
  const [source, setSource] = useState("");
  const [openOnly, setOpenOnly] = useState(true);
  const [groups, setGroups] = useState<OpsGroup[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [events, setEvents] = useState<OpsEvent[] | null>(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    setErr(null);
    try {
      const r = await api<{ groups: OpsGroup[] }>(`/portal/ops/summary?days=${days}`);
      setGroups(r.groups);
    } catch (e: any) {
      // A failure reading the failures is itself a failure — never an empty "all clear".
      setGroups(null); setErr(String(e.message || e));
    }
  }
  useEffect(() => { load(); }, [days]);

  async function expand(g: OpsGroup) {
    const key = `${g.source}/${g.kind}`;
    if (expanded === key) { setExpanded(null); setEvents(null); return; }
    setExpanded(key); setEvents(null);
    try {
      const r = await api<{ events: OpsEvent[] }>(
        `/portal/ops/events?days=${days}&source=${encodeURIComponent(g.source)}&kind=${encodeURIComponent(g.kind)}&limit=25`);
      setEvents(r.events);
    } catch (e: any) { setErr(String(e.message || e)); }
  }

  async function resolve(g: OpsGroup) {
    if (!(await confirmDialog(
      `Mark all ${g.open_count} open "${g.kind}" from ${OPS_SOURCE_LABEL[g.source] || g.source} as handled?\n\nThe history stays; they stop counting as open.`,
      { confirmLabel: "Mark handled" }))) return;
    setBusy(true);
    try { await api("/portal/ops/resolve", { method: "POST", body: JSON.stringify({ source: g.source, kind: g.kind }) }); await load(); }
    catch (e: any) { setErr(String(e.message || e)); }
    finally { setBusy(false); }
  }

  const shown = (groups || []).filter((g) => (!source || g.source === source) && (!openOnly || g.open_count > 0));
  const sources = Array.from(new Set((groups || []).map((g) => g.source)));
  const openErrors = (groups || []).filter((g) => g.severity === "error" && g.open_count > 0)
                                   .reduce((a, g) => a + g.open_count, 0);

  return (
    <div className="wrap">
      <div className="row" style={{ marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
        <h2 style={{ margin: 0 }}>Health</h2>
        {groups && (
          <span className="muted">
            {openErrors > 0 ? `${openErrors} open error${openErrors === 1 ? "" : "s"}` : "nothing open"}
          </span>
        )}
        <select className="ctl" style={{ maxWidth: 150, marginLeft: "auto" }} value={days}
                onChange={(e) => setDays(Number(e.target.value))}>
          <option value={1}>Last 24 hours</option>
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
        <label className="row" style={{ gap: 6 }}>
          <input type="checkbox" checked={openOnly} onChange={(e) => setOpenOnly(e.target.checked)} />
          Open only
        </label>
      </div>

      <div className="row" style={{ gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
        <button className={"btn sm" + (source === "" ? " accent" : "")} onClick={() => setSource("")}>All sources</button>
        {sources.map((sname) => (
          <button key={sname} className={"btn sm" + (source === sname ? " accent" : "")}
                  onClick={() => setSource(sname)}>{OPS_SOURCE_LABEL[sname] || sname}</button>
        ))}
      </div>

      {err && <div className="err" style={{ marginBottom: 12 }}>{err}</div>}

      <div className="card">
        {groups === null && !err && <div className="muted" style={{ padding: 14 }}>Loading…</div>}
        {groups !== null && shown.length === 0 && (
          <div className="muted" style={{ padding: 14 }}>
            {openOnly ? "Nothing open in this window. Untick “Open only” to see what has been handled."
                      : "Nothing recorded in this window."}
          </div>
        )}
        {shown.map((g) => {
          const key = `${g.source}/${g.kind}`;
          return (
            <div key={key} style={{ borderBottom: "1px solid var(--border)" }}>
              <div className="row" style={{ gap: 10, alignItems: "baseline", padding: "11px 14px", cursor: "pointer" }}
                   onClick={() => expand(g)}>
                <span style={{ width: 8, height: 8, borderRadius: 8, background: SEV_COLOR[g.severity], flex: "none" }} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="fld" style={{ marginBottom: 2 }}>
                    {g.kind}
                    <span className="muted" style={{ fontWeight: 400 }}> · {OPS_SOURCE_LABEL[g.source] || g.source}</span>
                  </div>
                  <div className="muted" style={{ fontSize: 12.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {g.latest_summary}
                  </div>
                </div>
                <span className="chip" style={{ fontVariantNumeric: "tabular-nums" }}>
                  {g.open_count > 0 ? `${g.open_count} open` : "handled"}
                  {g.occurrences !== g.open_count ? ` / ${g.occurrences}` : ""}
                </span>
                <span className="muted" style={{ fontSize: 12, whiteSpace: "nowrap" }}>
                  {g.last_at ? new Date(g.last_at).toLocaleString() : ""}
                </span>
                {g.open_count > 0 && (
                  <button className="btn sm" disabled={busy}
                          onClick={(e) => { e.stopPropagation(); resolve(g); }}>Mark handled</button>
                )}
              </div>
              {expanded === key && (
                <div style={{ padding: "0 14px 12px 32px" }}>
                  {events === null
                    ? <div className="muted" style={{ fontSize: 13 }}>Loading occurrences…</div>
                    : events.length === 0
                      ? <div className="muted" style={{ fontSize: 13 }}>No occurrences in this window.</div>
                      : events.map((ev) => (
                          <div key={ev.id} style={{ fontSize: 13, padding: "4px 0", borderTop: "1px solid var(--border)" }}>
                            <span className="muted" style={{ fontVariantNumeric: "tabular-nums" }}>
                              {ev.at ? new Date(ev.at).toLocaleString() : ""}
                            </span>{" "}
                            {ev.summary}
                            {ev.detail?.path ? <span className="muted"> · {ev.detail.path}</span> : null}
                            {ev.campaign_name ? <span className="muted"> · {ev.campaign_name}</span> : null}
                            {ev.login_id ? <span className="muted"> · {ev.login_id}</span> : null}
                          </div>
                        ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="muted" style={{ fontSize: 12, marginTop: 10 }}>
        Not everything here is a problem. A cancelled request is a canvasser walking out of signal;
        a sync write that failed is the database refusing our write, and that one has cost field
        days before. Everything is recorded; only some of it is worth chasing.
      </div>
    </div>
  );
}
